import React, { useEffect, useRef, useState } from 'react';
import businessMetricsTracker from './BusinessMetricsTracker';
import { SecurityManager } from './SecurityManager';
import { GlobalErrorHandler } from './GlobalErrorHandler';
import { MonitoringManager } from './MonitoringManager';
import { LoggingManager } from './LoggingManager';

// Analytics tracking functions (extraParams optional - use for calculator_tier etc.)
const trackEvent = (eventName, category, label, value, extraParams = {}) => {
    if (typeof window !== 'undefined') {
        const payload = {
            'event_category': category,
            'event_label': label,
            'value': value,
            ...extraParams
        };
        if (typeof window.gtag === 'function') {
            window.gtag('event', eventName, payload);
        } else if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ 'event': eventName, ...payload });
        }
    }
};

const trackCalculatorUsage = (calculatorType, action) => {
    trackEvent('calculator_usage', 'feature_usage', `${calculatorType}_${action}`, undefined, { calculator_tier: 'basic' });
    
    // Track in business metrics
    if (action === 'calculate_complete') {
        businessMetricsTracker.trackCalculatorUsage(calculatorType);
    }
};

// Track calculator tier page view
const trackCalculatorTierView = (tier) => {
    if (typeof gtag !== 'undefined') {
        gtag('event', 'calculator_tier_view', {
            'calculator_tier': tier,
            'event_category': 'Calculator Tier Usage',
            'timestamp': new Date().toISOString()
        });
        console.log('📊 GA4: Calculator tier viewed -', tier);
    }
};

const trackCalculationComplete = (calculatorType, inputs) => {
    trackFunnelCalculationComplete({
        calculatorTier: 'basic',
        calculatorType,
        platform: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop'
    });
    businessMetricsTracker.trackCalculatorUsage(calculatorType);
};

const trackFunnelEvent = (eventName, params = {}) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, params);
};
import { AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { SimulationEngine } from './SimulationEngine';
import { FinancialConfig, calculateDivision296Tax } from './FinancialConfig';
import {
  buildBasicCalcContext,
  computeBasicMSI,
  resolveBasicAdminFeePercentage,
  computeBasicAchievableYearIncome,
  runBasicLiveToAgeWhatIf,
  runBasicMoreIncomeWhatIf,
  runBasicAnnualFeesWhatIf,
  runBasicSequenceRiskWhatIf,
  runBasicSurvivingSpouseWhatIf,
} from './basicWhatIfEngine.js';

/** Basic calculator horizon when retirement period is missing or invalid — see FinancialConfig.CALCULATOR_DEFAULTS */
function resolveBasicRetirementPeriodYears(rawPeriod) {
  const n = parseInt(rawPeriod, 10);
  const fallback = FinancialConfig.CALCULATOR_DEFAULTS.BASIC_RETIREMENT_PERIOD_YEARS;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// Expose SimulationEngine globally for landing page API
if (typeof window !== 'undefined') {
  window.SimulationEngine = SimulationEngine;
  console.log('✓ SimulationEngine exposed to window');
}
import { MobileCurrencyInput, MobileAgeInput, MobileNumberInput, MobileSelect, MobileCheckbox, MobileButton } from './components/MobileOptimizedInput';
import MobileLoadingSpinner from './components/MobileLoadingSpinner';
import ResponsiveTable from './components/ResponsiveTable';
import { getResponsiveChartConfig, formatChartCurrency } from './utils/mobileChartConfig';
import { triggerHaptic } from './utils/hapticFeedback';
import { ScenarioWrapper } from './ScenarioWrapper';
import BasicTutorialGuide from './BasicTutorialGuide.js';
import DraggableTutorialPanel from './components/DraggableTutorialPanel.js';
import PDFExportManager from './PDFExportManager.js';
import CalculatorBridge from './CalculatorBridge.js';
import CookieConsent from './components/CookieConsent.js';
import { scrollToElement } from './utils/mobileScrollFix.js';
import { getLifeExpectancy, getYearsToLifeExpectancy } from './lifeExpectancyData.js';
import InfoTooltip from './components/InfoTooltip.js';
import { EmailCaptureForm } from './components/EmailCaptureForm.js';
import { AdvancedWalkthroughVideo } from './components/AdvancedWalkthroughVideo.js';
import CalculationTransparency from './components/CalculationTransparency.js';
import {
  buildBasicEmailResults,
  generateBasicCalculatorPdfBase64
} from './utils/basicCalculatorPdfEmail.js';
import { SubscriptionManager } from './SubscriptionManager.js';
import {
  trackFirstCalculatorInput,
  trackCalculationComplete as trackFunnelCalculationComplete,
  trackBasicAdvancedCtaShown
} from './utils/funnelAnalytics.js';
import { openPricingModal } from './utils/openPricingModal.js';
import {
  alertCheckoutFailure,
  startPdfUnlockCheckout,
} from './utils/recommendedCheckout.js';

// All fee calculations now use SimulationEngine.FeeManager (single source of truth)

function App() {
  // State for FOMO modal and inputs-revert explainer (free users)
  const [showFOMOModal, setShowFOMOModal] = useState(false);
  const [showInputsRevertModal, setShowInputsRevertModal] = useState(false);
  const [showInstructionalPopup, setShowInstructionalPopup] = useState(false);
  const [isFromLanding, setIsFromLanding] = useState(false);
  const [isFromMonteCarloDemo, setIsFromMonteCarloDemo] = useState(false);

  // Track calculator tier page view
  useEffect(() => {
    trackCalculatorTierView('basic');
  }, []);

  // Pre-fill from landing page if redirected
  useEffect(() => {
    console.log('🔍 App.js useEffect running, checking URL params...');
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 URL params:', window.location.search);
    console.log('🔍 demo param:', urlParams.get('demo'));
    console.log('🔍 from param:', urlParams.get('from'));
    
    // Handle demo=advanced for Monte Carlo blog
    if (urlParams.get('demo') === 'advanced' && urlParams.get('from') === 'monte_carlo') {
      console.log('🎯 Monte Carlo demo redirect detected');
      // Trigger loading the example scenario
      // This will be picked up by the advanced calculator component
      localStorage.setItem('loadExample', 'single-comprehensive');
      
      setIsFromMonteCarloDemo(true);
      console.log('🎯 Set isFromMonteCarloDemo to true');

      // Track automatic example scenario load from blog CTA
      try {
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'example_scenario_auto_load', {
            calculator_type: 'advanced',
            scenario_id: 'single-comprehensive',
            source: 'monte_carlo_blog'
          });
        }
      } catch (e) {
        console.error('GA tracking error (example_scenario_auto_load):', e);
      }
      
      // Show instructional popup to run simulation
      setTimeout(() => {
        console.log('🎯 Showing instructional popup for Monte Carlo demo');
        console.log('🎯 About to call setShowInstructionalPopup(true)');
        setShowInstructionalPopup(true);
        console.log('🎯 Called setShowInstructionalPopup(true)');
      }, 1500);
    }
    
    if (urlParams.get('from') === 'landing') {
      const savedSuper = localStorage.getItem('calc_super');
      const savedAge = localStorage.getItem('calc_age');
      const savedIncome = localStorage.getItem('calc_income');
      const savedReturn = localStorage.getItem('calc_return');
      const savedInflation = localStorage.getItem('calc_inflation');
      const savedYears = localStorage.getItem('calc_years');
      
      if (savedSuper) {
        console.log('📋 Pre-filling from landing page:', { savedSuper, savedAge, savedIncome, savedReturn, savedInflation, savedYears });
        
        // Pre-fill the inputs with landing page values
        setInputs(prev => ({
          ...prev,
          yourSuper: parseFloat(savedSuper) || prev.yourSuper,
          yourAge: parseInt(savedAge) || prev.yourAge,
          desiredAnnualIncome: parseFloat(savedIncome) || prev.desiredAnnualIncome,
          assumedReturn: parseFloat(savedReturn) || prev.assumedReturn,
          assumedInflation: parseFloat(savedInflation) || prev.assumedInflation,
          retirementPeriod: parseInt(savedYears) || prev.retirementPeriod
        }));
        
        setIsFromLanding(true);
        // Show instructional popup immediately
        setTimeout(() => {
          setShowInstructionalPopup(true);
        }, 500);
      }
      
      // Clear localStorage after reading
      localStorage.removeItem('calc_super');
      localStorage.removeItem('calc_age');
      localStorage.removeItem('calc_income');
      localStorage.removeItem('calc_return');
      localStorage.removeItem('calc_inflation');
      localStorage.removeItem('calc_years');
    }
  }, []);

  // Initialize production systems on app startup
  useEffect(() => {
    // Initialize security, error handling, and monitoring
    SecurityManager.initialize();
    GlobalErrorHandler.initialize();
    MonitoringManager.initialize();
    LoggingManager.initialize();
    
    // Log app startup
    LoggingManager.info('App component initialized', {
      component: 'App',
      timestamp: new Date().toISOString()
    });
    
    // Track app startup
    MonitoringManager.trackEvent('app_startup', {
      component: 'App',
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
    
    // ===== LANDING PAGE API: Listen for calculation requests via postMessage =====
    const handleCalculationRequest = (event) => {
      console.log('📨 Received postMessage:', event.data);
      
      // Security: Only accept messages from same origin
      if (event.origin !== window.location.origin) {
        console.warn('❌ Rejected calculation request from different origin:', event.origin);
        return;
      }
      
      const { type, requestId, params } = event.data;
      console.log('✓ Message accepted. Type:', type, 'RequestID:', requestId);
      
      if (type === 'CALCULATE_MSI') {
        // Calculate Maximum Sustainable Income
        try {
          const result = SimulationEngine.calculateMaxSustainableIncomeWithFees(
            params.initialBalance || 0,
            params.years || 30,
            params.netReturn || 0.05,
            params.otherIncome || 0,
            params.assessableAssets || 0,
            params.isCouple || false,
            params.isHomeowner || true,
            params.includePension !== false, // default true
            params.adminFees || 52,
            params.investmentFees || 0,
            params.insuranceFees || 0,
            params.targetFinalBalance || 0,
            params.adminFeePercentage || 0.10,
            params.pensionGrowthRate || 0,
            params.startingAge || 65
          );
          
          // Send result back to landing page
          event.source.postMessage({
            type: 'CALCULATION_RESULT',
            requestId,
            result: {
              maxSustainableIncome: result,
              success: true
            }
          }, event.origin);
          
          console.log(`✓ MSI calculation completed: $${result.toLocaleString()}`);
        } catch (error) {
          event.source.postMessage({
            type: 'CALCULATION_ERROR',
            requestId,
            error: error.message
          }, event.origin);
          console.error('MSI calculation error:', error);
        }
      } else if (type === 'CALCULATE_PENSION') {
        // Calculate Age Pension
        try {
          const result = SimulationEngine.calculatePension(
            params.totalAssets || 0,
            params.totalIncome || 0,
            params.isCouple || false,
            params.isHomeowner || true,
            params.year || 0,
            params.customGrowthRate || 0,
            params.rentPaidAnnual || 0
          );
          
          event.source.postMessage({
            type: 'CALCULATION_RESULT',
            requestId,
            result: {
              pension: result,
              success: true
            }
          }, event.origin);
          
          console.log(`✓ Pension calculation completed: $${result.toLocaleString()}`);
        } catch (error) {
          event.source.postMessage({
            type: 'CALCULATION_ERROR',
            requestId,
            error: error.message
          }, event.origin);
          console.error('Pension calculation error:', error);
        }
      } else if (type === 'CALCULATE_FINAL_BALANCE') {
        // Calculate final balance with specific income
        try {
          const result = SimulationEngine.calculateFinalBalanceWithIncome(
            params.initialBalance || 0,
            params.years || 30,
            params.income || 0,
            params.netReturn || 0.05,
            params.otherIncome || 0,
            params.assessableAssets || 0,
            params.isCouple || false,
            params.isHomeowner || true,
            params.includePension !== false,
            params.adminFees || 52,
            params.investmentFees || 0,
            params.insuranceFees || 0,
            params.adminFeePercentage || 0.10,
            params.targetFinalBalance || 0,
            params.pensionGrowthRate || 0,
            params.startingAge || 65
          );
          
          event.source.postMessage({
            type: 'CALCULATION_RESULT',
            requestId,
            result: {
              finalBalance: result,
              success: true
            }
          }, event.origin);
          
          console.log(`✓ Final balance calculation completed: $${result.toLocaleString()}`);
        } catch (error) {
          event.source.postMessage({
            type: 'CALCULATION_ERROR',
            requestId,
            error: error.message
          }, event.origin);
          console.error('Final balance calculation error:', error);
        }
      }
    };
    
    window.addEventListener('message', handleCalculationRequest);
    console.log('🎧 Message listener registered in React app');
    
    // Test listener is working
    setTimeout(() => {
      console.log('🧪 Testing if listener works...');
      window.postMessage({ type: 'TEST', test: true }, window.location.origin);
    }, 2000);
    
    return () => {
      console.log('🗑️ Removing message listener');
      window.removeEventListener('message', handleCalculationRequest);
    };
  }, []);

  const [showTutorial, setShowTutorial] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [validationErrors, setValidationErrors] = useState({}); // Track inline validation errors
  const exportMenuRef = useRef(null);

  // Track screen size for mobile optimization
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Default inputs (used for initial state and for reverting free users after run)
  const DEFAULT_BASIC_INPUTS = {
    relationshipStatus: 'single',
    yourAge: '',
    yourGender: 'male',
    yourSuper: '',
    partnerAge: '',
    partnerGender: 'female',
    partnerSuper: '',
    retirementPeriod: 25,
    desiredAnnualIncome: '',
    otherTaxableIncome: '',
    otherNonTaxableIncome: '',
    assessableAssets: '',
    isHomeowner: true,
    includePension: true,
    pensionGrowthRate: 0.3,
    assumedReturn: 6.5,
    assumedInflation: 2.5,
    adminFees: 52,
    adminFeePercentage: 0.10,
    investmentFees: 0.5,
    insuranceFees: '',
    whatIfLiveToAge: 100,
    whatIfMoreIncome: 10000,
    whatIfAnnualFees: 500,
    whatIfSequenceRisk: -20
  };

  const SCENARIO_PRESETS = {
    early:   { yourAge: 60, yourSuper: 400000, desiredAnnualIncome: 40000, retirementPeriod: 30 },
    typical: { yourAge: 65, yourSuper: 600000, desiredAnnualIncome: 55000, retirementPeriod: 25 },
    high:    { yourAge: 67, yourSuper: 900000, desiredAnnualIncome: 70000, retirementPeriod: 20 },
  };

  // Load saved inputs from LocalStorage on mount
  const loadSavedInputs = () => {
    // Check for transfer data first (from other calculators)
    const transferData = CalculatorBridge.loadTransferData('basic');
    if (transferData) {
      // Per-partner validation: Allow any age (for pre-retirement planning)
      return transferData;
    }

    // Otherwise load saved data (Pro only: save/restore is gated)
    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) return null;
    try {
      const saved = localStorage.getItem('basic_calculator_inputs');
      if (saved) {
        const data = JSON.parse(saved);
        // Per-partner validation: Allow any age for planning purposes
        return data;
      }
    } catch (err) {
      // Error loading saved inputs - handled silently
    }
    return null;
  };

  const resolveInitialBasicState = () => {
    const saved = loadSavedInputs();
    if (saved) return { inputs: saved, scenarioPreset: null };
    if (SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
      return { inputs: DEFAULT_BASIC_INPUTS, scenarioPreset: null };
    }
    return {
      inputs: { ...DEFAULT_BASIC_INPUTS, ...SCENARIO_PRESETS.typical },
      scenarioPreset: 'typical',
    };
  };

  const initialBasicState = resolveInitialBasicState();
  const [inputs, setInputs] = useState(initialBasicState.inputs);
  const [selectedScenarioPreset, setSelectedScenarioPreset] = useState(initialBasicState.scenarioPreset);
  const [selectedReturnPreset, setSelectedReturnPreset] = useState('medium');
  // Keep for backward compat with any remaining references
  const selectedRiskPreset = selectedScenarioPreset;

  const [chartData, setChartData] = useState([]);

  const RETURN_PRESETS = {
    low:    { assumedReturn: 4.5 },
    medium: { assumedReturn: 6.5 },
    high:   { assumedReturn: 8.5 },
  };

  const applyScenarioPreset = (scenario) => {
    const updates = SCENARIO_PRESETS[scenario];
    if (!updates) return;
    setSelectedScenarioPreset(scenario);
    setInputs((prev) => ({ ...prev, ...updates }));
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'preset_selected', { event_category: 'Calculator Usage', event_label: `basic_scenario_${scenario}`, calculator_tier: 'basic' });
    }
  };

  const applyReturnPreset = (level) => {
    const updates = RETURN_PRESETS[level];
    if (!updates) return;
    setSelectedReturnPreset(level);
    setInputs((prev) => ({ ...prev, ...updates }));
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'preset_selected', { event_category: 'Calculator Usage', event_label: `basic_return_${level}`, calculator_tier: 'basic' });
    }
  };

  // Legacy shim — keep existing applyRiskPreset calls working
  const applyRiskPreset = applyScenarioPreset;

  const [hasRunProjection, setHasRunProjection] = useState(false);
  const [lastRunDesiredIncome, setLastRunDesiredIncome] = useState(0);
  const [whatIfResults, setWhatIfResults] = useState({
    liveToAge: null,
    moreIncome: null,
    annualFees: null,
    sequenceRisk: null,
    survivingSpouse: null
  });
  const [lastSaved, setLastSaved] = useState(null);
  const [sessionStartTime] = useState(Date.now());

  const projectionResultsRef = useRef(null);
  const runButtonRef = useRef(null);
  const firstCalculatorInputFiredRef = useRef(false);
  const lastRunInputsRef = useRef(null);
  const lastRunMSIRef = useRef(0);
  const autoTypicalPresetTrackedRef = useRef(false);

  const getInputsForAdvancedTransfer = () => lastRunInputsRef.current ?? inputs;

  useEffect(() => {
    if (initialBasicState.scenarioPreset !== 'typical' || autoTypicalPresetTrackedRef.current) return;
    autoTypicalPresetTrackedRef.current = true;
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'preset_selected', {
        event_category: 'Calculator Usage',
        event_label: 'basic_scenario_typical_auto',
        calculator_tier: 'basic',
      });
    }
  }, []);

  // Track Basic → retirement-calculator CTA exposure once results are ready.
  // Do not auto-scroll to a second upsell — the primary handoff sits in results.
  useEffect(() => {
    if (!hasRunProjection || !chartData.length) return;
    trackBasicAdvancedCtaShown({ surface: 'results_primary', platform: isMobile ? 'mobile' : 'desktop' });
  }, [hasRunProjection, chartData, isMobile]);

  // Check for transfer data on mount
  useEffect(() => {
    const transferData = CalculatorBridge.loadTransferData('basic');
    if (transferData) {
      setInputs(prev => ({
        ...prev,
        ...transferData
      }));
      // Clear transfer data after loading
      CalculatorBridge.clearTransferData('basic');
    }
  }, []);

  // Autosave inputs to LocalStorage whenever they change (Pro only: save/restore is gated)
  useEffect(() => {
    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) return;
    try {
      localStorage.setItem('basic_calculator_inputs', JSON.stringify(inputs));
      setLastSaved(new Date());
    } catch (err) {
      // Error saving inputs - handled silently
    }
  }, [inputs]);
  
  // Update autosave indicator every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastSaved) {
        // Force re-render to update "X seconds ago"
        setLastSaved(prev => prev);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lastSaved]);

  // Show FOMO modal after results – wait until inputs-revert modal is closed so they don't overlap
  useEffect(() => {
    if (!hasRunProjection) return;
    // Free users see the revert modal first; only start FOMO timer after they've closed it
    if (showInputsRevertModal) return;
    const timer = setTimeout(() => {
      setShowInstructionalPopup(false);
      setShowFOMOModal(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [hasRunProjection, showInputsRevertModal]);

  // Track session time when component unmounts
  useEffect(() => {
    return () => {
      const sessionTime = (Date.now() - sessionStartTime) / 1000 / 60; // minutes
      if (sessionTime > 0.5) { // Only track sessions longer than 30 seconds
        businessMetricsTracker.trackTimeInApp(sessionTime);
      }
    };
  }, [sessionStartTime]);

  // Clear saved data function
  const clearSavedData = () => {
    if (window.confirm('Clear all saved data and reset to defaults? This cannot be undone.')) {
      localStorage.removeItem('basic_calculator_inputs');
      window.location.reload();
    }
  };

  const handleInputChange = (field, value) => {
    // Track input changes for analytics
    trackEvent('input_change', 'user_interaction', field, undefined, { calculator_tier: 'basic' });
    trackFirstCalculatorInput({
      calculatorTier: 'basic',
      platform: isMobile ? 'mobile' : 'desktop',
      interaction: 'edit'
    });
    // Security: Sanitize input to prevent XSS
    const sanitizedValue = SecurityManager.sanitizeInput(value);
    
    // Log input change for monitoring
    MonitoringManager.trackEvent('input_change', {
      field: field,
      value: typeof sanitizedValue === 'string' ? sanitizedValue.substring(0, 50) : sanitizedValue, // Truncate for privacy
      timestamp: new Date().toISOString()
    });
    
    // Handle string values for partial decimal input (e.g., "7." or ".5")
    if (typeof sanitizedValue === 'string' && (sanitizedValue === '' || sanitizedValue === '.' || sanitizedValue.endsWith('.'))) {
      setInputs(prev => ({ ...prev, [field]: sanitizedValue }));
      return;
    }
    
    // Validation for numeric fields
    const errors = { ...validationErrors };
    let hasBlockingError = false;
    
    if (typeof sanitizedValue === 'number' || !isNaN(sanitizedValue)) {
      const numValue = typeof sanitizedValue === 'number' ? sanitizedValue : parseFloat(sanitizedValue);
      
      // Age validation - REGULATORY COMPLIANCE (Per-Partner)
      if (field === 'yourAge' || field === 'partnerAge') {
        if (numValue < 0) {
          errors[field] = 'Age cannot be negative';
          hasBlockingError = true;
        } else if (numValue > 120) {
          errors[field] = `Age ${numValue} seems unrealistic (max 120)`;
          hasBlockingError = true;
        } else {
          delete errors[field];
        }
      }
      
      // Super balance validation
      if (field === 'yourSuper' || field === 'partnerSuper') {
        if (numValue < 0) {
          errors[field] = 'Super balance cannot be negative';
          hasBlockingError = true;
        } else {
          delete errors[field];
        }
      }
      
      // Desired Annual Income validation
      if (field === 'desiredAnnualIncome') {
        if (numValue < 0) {
          errors[field] = 'Income cannot be negative';
          hasBlockingError = true;
        } else {
          delete errors[field];
        }
      }
      
      // Retirement Period validation
      if (field === 'retirementPeriod') {
        if (numValue <= 0) {
          errors[field] = 'Retirement period must be at least 1 year';
          hasBlockingError = true;
        } else if (numValue > FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE) {
          errors[field] = `⚠️ Very long period (${numValue} years — that's a warning, not an error)`;
        } else {
          delete errors[field];
        }
      }
      
      // Other Assets validation
      if (field === 'assessableAssets') {
        if (numValue < 0) {
          errors[field] = 'Assets cannot be negative';
          hasBlockingError = true;
        } else {
          delete errors[field];
        }
      }
      
      // Other Income validation
      if (field === 'otherTaxableIncome' || field === 'otherNonTaxableIncome') {
        if (numValue < 0) {
          errors[field] = 'Income cannot be negative';
          hasBlockingError = true;
        } else {
          delete errors[field];
        }
      }
    }
    
    setValidationErrors(errors);
    
    // Don't update state if blocking error
    if (hasBlockingError) {
      return;
    }
    
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
    // Only reset projection when main inputs change, not what-if inputs
    if (!field.startsWith('whatIf')) {
      setHasRunProjection(false);
    }
    
    // Age Pension eligibility check - shown inline where needed
    if (field === 'includePension') {
      // Removed alert — eligibility indication now shown inline in form
    }
    
    // Scroll to Run button when Include Pension is checked or unchecked
    if (field === 'includePension') {
      scrollToElement(runButtonRef, { delay: 100, offset: 60 });
    }
  };

  // Helper function to display input values (improved for mobile)
  const getInputValue = (value) => {
    // For mobile compatibility, always show the actual value
    // This prevents issues where users can't delete the first digit
    // Handle both numbers and strings (for partial decimal input)
    if (value === 0) return '0';
    if (value === '') return '';
    if (value === '.') return '.';
    return value || '';
  };

  const runProjection = () => {
    try {
      // Show loading spinner
      setIsCalculating(true);
      
      // Track calculation attempt
      trackCalculatorUsage('basic_retirement', 'calculate_attempt');
      trackFunnelEvent('calculator_started', {
        calculator_type: 'basic_retirement',
        calculator_tier: 'basic',
        ab_variant: window._abVariant || 'control'
      });
      MonitoringManager.trackEvent('calculation_start', {
        calculator: 'basic_retirement',
        timestamp: new Date().toISOString()
      });
      
      // Generate actual chart data using user inputs
      const projectionData = generateChartData();
      
      // If validation failed, generateChartData returns null
      if (projectionData === null) {
        trackCalculatorUsage('basic_retirement', 'validation_error');
        MonitoringManager.trackEvent('calculation_validation_error', {
          calculator: 'basic_retirement',
          timestamp: new Date().toISOString()
        });
        LoggingManager.warn('Calculation validation failed', {
          calculator: 'basic_retirement',
          inputs: inputs
        });
        setIsCalculating(false);
        return;
      }
      
      setChartData(projectionData);
      setHasRunProjection(true);
      lastRunMSIRef.current = Math.round(projectionData[0]?.maxSustainableIncome || projectionData[0]?.totalIncome || 0);
      // Capture desired income used in this run before inputs may be reset
      setLastRunDesiredIncome(Number(inputs.desiredAnnualIncome) || 0);
      lastRunInputsRef.current = { ...inputs };
      // Free users: revert inputs to defaults after run (subscribers keep theirs)
      if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
        setInputs({ ...DEFAULT_BASIC_INPUTS });
        setShowInputsRevertModal(true); // Explain revert + subscriber persistence
      }
      
      trackCalculationComplete('basic_retirement', inputs);
      trackFunnelEvent('calculator_completed', {
        calculator_type: 'basic_retirement',
        calculator_tier: 'basic',
        ab_variant: window._abVariant || 'control'
      });
      MonitoringManager.trackEvent('calculation_success', {
        calculator: 'basic_retirement',
        timestamp: new Date().toISOString()
      });
      LoggingManager.info('Calculation completed successfully', {
        calculator: 'basic_retirement',
        inputs: inputs
      });
      
      // Hide loading spinner after a brief delay to ensure smooth UX
      setTimeout(() => setIsCalculating(false), 500);
      
    } catch (error) {
      // Track calculation error
      MonitoringManager.trackEvent('calculation_error', {
        calculator: 'basic_retirement',
        error: error.message,
        timestamp: new Date().toISOString()
      });
      LoggingManager.error('Calculation failed', {
        calculator: 'basic_retirement',
        error: error.message,
        stack: error.stack,
        inputs: inputs
      });
      
      // Hide loading spinner and show user-friendly error message
      setIsCalculating(false);
      
      // Show inline error instead of alert (Issue #7 fix: remove intrusive dialogs)
      setValidationErrors({
        calculationError: 'An error occurred during calculation. Please check your inputs and try again.'
      });
      
      // Scroll to errors
      setTimeout(() => {
        scrollToElement(projectionResultsRef, { delay: 100, offset: 60 });
      }, 100);
    }
    
    // Scroll to Projection Results after running projection (mobile-friendly)
    scrollToElement(projectionResultsRef, { delay: 400, offset: 80 });
  };

  // What-if calculation functions - All values in real terms (today's dollars)
  const getBasicWhatIfContext = () => {
    const snap = lastRunInputsRef.current || inputs;
    return buildBasicCalcContext(snap, getAfterTaxIncome);
  };

  const getBasicBaselineMSI = () => {
    const fromRun = lastRunMSIRef.current;
    if (fromRun > 0) return fromRun;
    return Math.round(computeBasicMSI(getBasicWhatIfContext()));
  };

  const runLiveToAgeWhatIf = () => {
    const ctx = getBasicWhatIfContext();
    const baseline = getBasicBaselineMSI();
    const result = runBasicLiveToAgeWhatIf(ctx, inputs.whatIfLiveToAge, baseline);
    setWhatIfResults(prev => ({ ...prev, liveToAge: result }));
  };

  const runMoreIncomeWhatIf = () => {
    const ctx = getBasicWhatIfContext();
    const baseline = getBasicBaselineMSI();
    const result = runBasicMoreIncomeWhatIf(ctx, inputs.whatIfMoreIncome, baseline);
    setWhatIfResults(prev => ({ ...prev, moreIncome: result }));
  };

  const runAnnualFeesWhatIf = () => {
    try {
      const ctx = getBasicWhatIfContext();
      const baseline = getBasicBaselineMSI();
      const result = runBasicAnnualFeesWhatIf(ctx, Number(inputs.whatIfAnnualFees) || 0, baseline);
      setWhatIfResults(prev => ({ ...prev, annualFees: result }));
    } catch (err) {
      console.error('Annual Fees WhatIf failed:', err);
      setWhatIfResults(prev => ({
        ...prev,
        annualFees: { error: (err && err.message) || 'Failed to run Annual Fees What-If' },
      }));
    }
  };

  const runSequenceRiskWhatIf = () => {
    const ctx = getBasicWhatIfContext();
    const baseline = getBasicBaselineMSI();
    const result = runBasicSequenceRiskWhatIf(ctx, inputs.whatIfSequenceRisk || 0, baseline);
    setWhatIfResults(prev => ({ ...prev, sequenceRisk: result }));
  };

  const runSurvivingSpouseWhatIf = () => {
    const ctx = getBasicWhatIfContext();
    const result = runBasicSurvivingSpouseWhatIf(ctx, getAfterTaxIncome);
    setWhatIfResults(prev => ({ ...prev, survivingSpouse: result }));
  };

  // Australian income tax calculation using FinancialConfig (loaded from JSON)
  const calculateTax = (income, { isCouple = false, hasPrivateHealthInsurance = true } = {}) => {
    let tax = 0;
    // Calculate tax using brackets from FinancialConfig.INCOME_TAX_BRACKETS
    const brackets = FinancialConfig.INCOME_TAX_BRACKETS;
    for (const bracket of brackets) {
      if (income > bracket.min) {
        const taxableInBracket = Math.min(income, bracket.max) - bracket.min;
        tax += taxableInBracket * bracket.rate;
      }
    }
    
    let lito = 0;
    if (income <= 37500) lito = 700;
    else if (income <= 45000) lito = 700 - (income - 37500) * 0.05;
    else if (income <= 66667) lito = 325 - (income - 45000) * 0.015;
    tax = Math.max(0, tax - lito);
    
    const baseMedicareLevy = income * 0.02;
    let surcharge = 0;
    if (!hasPrivateHealthInsurance) {
      const singleThresholds = { tier1: 97000, tier2: 113000, tier3: 151000 };
      const familyThresholds = { tier1: 194000, tier2: 226000, tier3: 302000 };
      const thresholds = isCouple ? familyThresholds : singleThresholds;
      if (income > thresholds.tier3) surcharge = income * 0.015;
      else if (income > thresholds.tier2) surcharge = income * 0.0125;
      else if (income > thresholds.tier1) surcharge = income * 0.01;
    }
    return tax + baseMedicareLevy + surcharge;
  };

  // Calculate after-tax income for pension calculations
  const getAfterTaxIncome = (taxableIncome, isCouple) => {
    if (!taxableIncome || taxableIncome <= 0) return 0;
    const tax = calculateTax(taxableIncome, { isCouple, hasPrivateHealthInsurance: true });
    const afterTax = Math.max(0, taxableIncome - tax);

    return afterTax;
  };

  // All pension calculations now use SimulationEngine (single source of truth)
  // REMOVED: const calculatePension_OLD = (totalAssets, totalIncome, isCouple, isHomeowner, year = 0) => {
    /* REMOVED - Using SimulationEngine.calculatePension instead
    // Apply pension growth over time
    const pensionGrowthMultiplier = Math.pow(1 + (inputs.pensionGrowthRate / 100), year);
    
    // 2024/2025 Centrelink thresholds (updated September 2024)
    const thresholds = {
      // Income test thresholds (per fortnight) - 2024/2025 rates
      incomeFreeThreshold: isCouple ? 380 : 218,
      incomeCutOff: isCouple ? 3934 : 2575.40,
      
      // Asset test thresholds (legacy comment only — not executed)
      assetFreeThreshold: isCouple ? 
        (isHomeowner ? 499000 : 766000) : 
        (isHomeowner ? 333000 : 600000),
      assetCutOff: isCouple ? 
        (isHomeowner ? 1102500 : 1369500) : 
        (isHomeowner ? 733500 : 1000500),
      
      // Deeming thresholds and rates
      lowDeemThresh: isCouple ? 106200 : 64200,
      lowDeemRate: 0.01,  // 1% for first threshold
      highDeemRate: 0.03, // 3% for excess
      
      // Maximum pension rates (per fortnight) - Updated for 2024/2025
      maxPension: isCouple ? 1777 : 1149, // ~$46k annual for couples
      
      // Supplements (per fortnight)
      pensionSupplement: isCouple ? 121 : 80.20,
      energySupplement: isCouple ? 21.20 : 14.10
    };
    
    // Apply growth multiplier to monetary values
    const maxPension = thresholds.maxPension * pensionGrowthMultiplier;
    const incomeFreeThreshold = thresholds.incomeFreeThreshold; // Keep constant - don't apply growth
    const incomeCutOff = thresholds.incomeCutOff; // Keep constant - don't apply growth
    
    // Free areas stay CPI-flat in real terms; cut-off moves with the maximum rate.
    const assetFreeThreshold = thresholds.assetFreeThreshold;
    const assetCutOff = thresholds.assetCutOff * pensionGrowthMultiplier;
    const pensionSupplement = thresholds.pensionSupplement * pensionGrowthMultiplier;
    const energySupplement = thresholds.energySupplement * pensionGrowthMultiplier;
    
    // Calculate deemed income from financial assets
    const deemed = totalAssets > thresholds.lowDeemThresh ? 
      thresholds.lowDeemThresh * thresholds.lowDeemRate + (totalAssets - thresholds.lowDeemThresh) * thresholds.highDeemRate : 
      totalAssets * thresholds.lowDeemRate;
    
    // Convert annual income to fortnightly for income test
    const fortnightlyIncome = totalIncome / 26;
    const fortnightlyDeemed = deemed / 26;
    const totalFortnightlyIncome = fortnightlyIncome + fortnightlyDeemed;
    
    // ASSET TEST
    let assetPension = maxPension;
    if (totalAssets > assetFreeThreshold) {
      // Reduce by $3 per fortnight for every $1,000 over threshold
      const reduction = ((totalAssets - assetFreeThreshold) / 1000) * 3;
      assetPension = Math.max(0, maxPension - reduction);
    }
    if (totalAssets >= assetCutOff) {
      assetPension = 0;
    }
    
    // INCOME TEST
    let incomePension = maxPension;
    if (totalFortnightlyIncome > incomeFreeThreshold) {
      // Reduce by 50 cents per dollar over threshold for both singles and couples (official rate)
      const reduction = (totalFortnightlyIncome - incomeFreeThreshold) * 0.5;
      incomePension = Math.max(0, maxPension - reduction);
      }
    if (totalFortnightlyIncome >= incomeCutOff) {
      incomePension = 0;
      }
    
    // Apply the test that results in the lower pension
    const basePension = Math.min(assetPension, incomePension);
    // Use base pension only (supplements excluded as per user's expected pension amount of $33,641)
    let finalPension = basePension;
    
    // Convert back to annual amount
    const annualPension = finalPension * 26;
    
    return annualPension;
  };
  */

  // Legacy getPensionParams_OLD removed — use SimulationEngine / getAgePensionParams (FinancialConfig).

  // Generate chart data - All values in real terms (today's dollars)
  const generateChartData = () => {
    // Per-partner age validation - allow any age but inform about eligibility
    const yourAge = inputs.yourAge || 0;
    const partnerAge = inputs.partnerAge || 0;
    const isCouple = inputs.relationshipStatus === 'couple';
    
    // Determine retirement eligibility age (youngest partner must reach 60)
    const youngestAge = isCouple ? Math.min(yourAge, partnerAge || yourAge) : yourAge;
    const oldestAge = isCouple ? Math.max(yourAge, partnerAge || yourAge) : yourAge;
    
    // Calculate years until retirement can start (when youngest reaches preservation age)
    const preservationAge = FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE;
    const yearsUntilRetirement = Math.max(0, preservationAge - youngestAge);
    
    // For Age Pension (both partners must be 67+ for couple pension, or calculate separately)
    const pensionAge = FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE;
    const youngestPensionAge = isCouple ? Math.min(yourAge, partnerAge || yourAge) : yourAge;
    const yearsUntilPension = Math.max(0, pensionAge - youngestPensionAge);
    
    // CRITICAL FIX: Only include partnerSuper if relationship status is 'couple'
    const totalSuper = (inputs.yourSuper || 0) + (isCouple ? (inputs.partnerSuper || 0) : 0);
    
    // DEBUG: Log totalSuper calculation
    
    const annualReturn = (inputs.assumedReturn || 0) / 100;
    const inflation = (inputs.assumedInflation || 0) / 100;
    const netReturn = annualReturn - inflation; // Real return (inflation-adjusted)
    const retirementPeriod = resolveBasicRetirementPeriodYears(inputs.retirementPeriod);
    const afterTaxIncome = getAfterTaxIncome(inputs.otherTaxableIncome || 0, isCouple);
    const otherIncome = afterTaxIncome + (inputs.otherNonTaxableIncome || 0); // Combined other income in real terms (after tax)
    const assessableAssets = inputs.assessableAssets || 0;
    const isHomeowner = inputs.isHomeowner;
    const includePension = inputs.includePension;
    const desiredIncome = inputs.desiredAnnualIncome || 0;

    const calcCtx = buildBasicCalcContext(inputs, getAfterTaxIncome);
    const maxSustainableIncome = computeBasicMSI(calcCtx);
    const adminFees = calcCtx.adminFees;
    const investmentFees = calcCtx.investmentFees;
    const insuranceFees = calcCtx.insuranceFees;
    const adminFeePercentage = calcCtx.adminFeePercentage;

    const data = [];
    let balanceMSI = totalSuper;
    let balanceDesired = totalSuper;

    for (let year = 0; year < retirementPeriod; year++) {
      const params = {
        netReturn,
        otherIncome,
        assessableAssets,
        isCouple,
        isHomeowner,
        includePension,
        adminFees,
        investmentFees,
        insuranceFees,
        adminFeePercentage,
        pensionGrowthRate: inputs.pensionGrowthRate,
        startingAge: yourAge,
        partnerStartingAge: isCouple ? partnerAge : null,
        memberShares: calcCtx.memberShares
      };

      const startingBalanceMSI = balanceMSI;
      const startingBalanceDesired = balanceDesired;

      const msiResult = SimulationEngine._simulateOneYear(balanceMSI, maxSustainableIncome, year, params);
      balanceMSI = msiResult.finalBalance;
      const withdrawalFromSuperMSI = msiResult.withdrawal;
      const calculatedPensionMSI = msiResult.pension;

      const desiredResult = SimulationEngine._simulateOneYear(balanceDesired, desiredIncome, year, params);
      balanceDesired = desiredResult.finalBalance;
      const withdrawalFromSuperDesired = desiredResult.withdrawal;
      const calculatedPensionDesired = desiredResult.pension;

      const afterTaxIncomeRow = getAfterTaxIncome(inputs.otherTaxableIncome || 0, isCouple);
      const otherNonTaxableIncome = inputs.otherNonTaxableIncome || 0;

      data.push({
        year: year + 1,
        balance: Math.round(startingBalanceMSI),
        endingBalance: Math.round(balanceMSI),
        balanceWithDesiredIncome: Math.round(startingBalanceDesired),
        withdrawal: Math.round(withdrawalFromSuperMSI),
        totalIncome: computeBasicAchievableYearIncome(msiResult, otherIncome),
        maxSustainableIncome: Math.round(maxSustainableIncome),
        pension: Math.round(calculatedPensionMSI),
        otherIncome: Math.round(otherIncome),
        otherTaxableIncome: Math.round(afterTaxIncomeRow),
        otherNonTaxableIncome: Math.round(otherNonTaxableIncome),
        withdrawalFromSuper: Math.round(withdrawalFromSuperMSI),
        withdrawalFromSuperDesired: Math.round(withdrawalFromSuperDesired),
        pensionDesired: Math.round(calculatedPensionDesired)
      });
    }
    
    // Add final data point showing ending balance after last year's withdrawal
    // This ensures the graph shows super depleting to $0 at the end
    if (data.length > 0) {
      const lastYear = data[data.length - 1];
      
      // Calculate ending balance for desired income scenario too
      // We need to simulate one more year to get the ending balance
      let endingBalanceDesired = balanceDesired;
      
      data.push({
        year: retirementPeriod + 1,
        balance: lastYear.endingBalance, // Ending balance of final year for MSI
        endingBalance: lastYear.endingBalance,
        balanceWithDesiredIncome: endingBalanceDesired, // Ending balance for target income scenario
        withdrawal: 0,
        totalIncome: 0,
        pension: 0,
        otherIncome: 0,
        otherTaxableIncome: 0,
        otherNonTaxableIncome: 0,
        withdrawalFromSuper: 0,
        withdrawalFromSuperDesired: 0,
        pensionDesired: 0
      });
    }
    
    return data;
  };

  // Chart data is now generated only when button is pressed
  
  // Scenario Management Functions
  const getCurrentInputs = () => {
    return { ...inputs };
  };

  const getCurrentResults = () => {
    if (!hasRunProjection || !chartData.length) {
      return null;
    }

    const snap = lastRunInputsRef.current || inputs;
    const calcCtx = buildBasicCalcContext(snap, getAfterTaxIncome);
    const maxSustainableIncome = lastRunMSIRef.current || Math.round(computeBasicMSI(calcCtx));

    return {
      maxSustainableIncome,
      finalBalance: chartData[chartData.length - 1]?.balance || 0,
      chartData: chartData,
      whatIfResults: whatIfResults
    };
  };

  const loadScenarioInputs = (scenarioInputs) => {
    // Ensure all inputs have proper default values to prevent null/undefined issues
    const defaultInputs = {
      relationshipStatus: 'single',
      yourAge: 60,
      yourSuper: 500000,
      partnerAge: 35,
      partnerSuper: 0,
      retirementPeriod: 25,
      desiredAnnualIncome: 60000,
      otherTaxableIncome: 0,
      otherNonTaxableIncome: 0,
      assessableAssets: 0,
      isHomeowner: true,
      includePension: true,
      pensionGrowthRate: 0.3,
      assumedReturn: 7.5,
      assumedInflation: 2.5,
      adminFees: 52,
      adminFeePercentage: 0.10,
      investmentFees: 0.5,
      insuranceFees: 0,
      whatIfLiveToAge: 100,
      whatIfMoreIncome: 10000,
      whatIfAnnualFees: 500,
      whatIfSequenceRisk: -20
    };
    
    const mergedInputs = { ...defaultInputs, ...scenarioInputs };
    setInputs(mergedInputs);
    setHasRunProjection(false); // Reset projection state
  };


    
    return (
    <ScenarioWrapper
      calculatorType="basic"
      getCurrentInputs={getCurrentInputs}
      getCurrentResults={getCurrentResults}
      loadScenarioInputs={loadScenarioInputs}
      hasResults={hasRunProjection}
    >
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-center flex-1" ref={projectionResultsRef} id="main-heading">2. Simple Retirement Snapshot</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowTutorial(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <span>🎓</span>
              <span>Tutorial</span>
            </button>
            {SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) ? (
              <button
                onClick={clearSavedData}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center space-x-2"
                title="Clear all saved data and reset"
              >
                <span>🗑️</span>
                <span>Clear Data</span>
              </button>
            ) : (
              <span className="text-sm text-gray-500 italic">Subscribe to save and restore your inputs across sessions.</span>
            )}
          </div>
        </div>

        {!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
          <div className="mb-6 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-4 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-amber-700 mb-1">Ready to use your own numbers properly?</p>
                <h2 className="text-xl font-bold text-gray-900">Unlock Advanced Retirement for unlimited Australian stress tests.</h2>
                <p className="text-sm text-gray-700 mt-1">
                  Save your inputs, run historical crashes and Monte Carlo, compare withdrawal strategies, and export a PDF/CSV report.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => openPricingModal({
                    source: 'app_top_paid_cta',
                    calculatorTier: 'basic',
                    surface: 'app_top_banner',
                  })}
                  className="px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition shadow"
                >
                  Unlock Advanced — from $14.99/mo
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* VALUE PROP: Hook before form (Step 1→2 Fix) */}
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-600 p-6 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">See How Long Your Super Will Last</h2>
          <p className="text-gray-700 mb-4">In just 30 seconds, find out if your current super and Age Pension will fund your retirement. No complex forms—just the essentials.</p>
          <div className="flex gap-2 flex-wrap">
            {!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
              <button
                onClick={() => {
                  // Load example scenario and run
                  const exampleInputs = {
                    relationshipStatus: 'single',
                    yourAge: 60,
                    yourSuper: 500000,
                    retirementPeriod: 25,
                    desiredAnnualIncome: 60000,
                    otherTaxableIncome: 0,
                    otherNonTaxableIncome: 0,
                    assessableAssets: 0,
                    isHomeowner: true,
                    includePension: true,
                    assumedReturn: 7.5,
                    assumedInflation: 2.5,
                  };
                  const runSnapshot = { ...inputs, ...exampleInputs };
                  setInputs(prev => ({ ...prev, ...exampleInputs }));
                  // Trigger calculation after inputs are set
                  setTimeout(() => {
                    setIsCalculating(true);
                    setTimeout(() => {
                      const projData = generateChartData();
                      if (projData) {
                        setChartData(projData);
                        setHasRunProjection(true);
                        setLastRunDesiredIncome(Number(runSnapshot.desiredAnnualIncome) || 0);
                        lastRunInputsRef.current = { ...runSnapshot };
                        if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
                          setInputs({ ...DEFAULT_BASIC_INPUTS });
                          setShowInputsRevertModal(true);
                        }
                        trackCalculationComplete('basic_retirement', runSnapshot);
                        setTimeout(() => {
                          scrollToElement(projectionResultsRef, { delay: 100, offset: 60 });
                        }, 300);
                      }
                      setIsCalculating(false);
                    }, 100);
                  }, 50);
                }}
                className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2"
              >
                <span>⚡</span> See Example Result
              </button>
            )}
            <p className="text-xs text-gray-600 italic self-center">or enter your details below</p>
          </div>
        </div>

        {/* Quick-start block — hidden for presets_shown=variant A/B test arm */}
        <div className={`mb-4 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4${window._abAssignments?.presets_shown === 'variant' ? ' hidden' : ''}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-bold text-gray-800">See the calculator in action, or enter your own numbers</p>
              <p className="text-xs text-gray-600 mt-0.5">
                <span className="font-semibold text-blue-700">Example scenarios</span> — not your real numbers, just pre-filled illustrations so you can run the calculator immediately and see how it works.&nbsp;
                <span className="font-semibold text-gray-700">Or scroll down</span> and fill in your own details for a personalised result.
              </p>
            </div>
            <p className="text-xs text-emerald-700 font-semibold whitespace-nowrap">
              {selectedScenarioPreset === 'typical' ? 'Typical retiree loaded — switch example or edit below' : 'Pick an example or fill in your own'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-600 mb-1">Example profile <span className="font-normal text-gray-400">(illustrative — not your real numbers)</span></p>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'early',   label: 'Early retiree',   sublabel: '60 · $400k · $40k/yr' },
                  { key: 'typical', label: 'Typical retiree', sublabel: '65 · $600k · $55k/yr' },
                  { key: 'high',    label: 'High balance',    sublabel: '67 · $900k · $70k/yr' },
                ].map((s) => (
                  <button key={s.key} type="button" onClick={() => applyScenarioPreset(s.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition text-left ${selectedScenarioPreset === s.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                    <span className="font-medium block">{s.label}</span>
                    <span className={`text-xs ${selectedScenarioPreset === s.key ? 'text-blue-100' : 'text-gray-500'}`}>{s.sublabel}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Return assumption</p>
              <div className="flex gap-2">
                {[
                  { key: 'low',    label: 'Low',    sublabel: '4.5%' },
                  { key: 'medium', label: 'Medium', sublabel: '6.5%' },
                  { key: 'high',   label: 'High',   sublabel: '8.5%' },
                ].map((r) => (
                  <button key={r.key} type="button" onClick={() => applyReturnPreset(r.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition text-left ${selectedReturnPreset === r.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'}`}>
                    <span className="font-medium block">{r.label}</span>
                    <span className={`text-xs ${selectedReturnPreset === r.key ? 'text-teal-100' : 'text-gray-500'}`}>{r.sublabel}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column */}
    <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Your Details</h2>
            
                                <div className="space-y-4">
              {/* Relationship Status */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship Status
                                        </label>
                <select
                  value={inputs.relationshipStatus}
                  onChange={(e) => handleInputChange('relationshipStatus', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="single">Single</option>
                  <option value="couple">Couple</option>
                </select>
                                    </div>

              {/* Couple Mode Explanation */}
              {inputs.relationshipStatus === 'couple' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-blue-900 mb-2">📋 How Couple Mode Works:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>✓ <strong>Super combined:</strong> Both partners' super combined for income calculation</li>
                    <li>✓ <strong>Age Pension assessed per person:</strong> Each partner is tested separately for eligibility, then household outcomes are combined</li>
                    <li>✓ <strong>Same retirement date assumed:</strong> Both retire at same time (if different, Advanced calculator models real timeline)</li>
                    <li>✓ <strong>Results show combined:</strong> Total income both can sustainably withdraw together</li>
                  </ul>
                </div>
              )}

              {/* Your Age */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  {inputs.relationshipStatus === 'couple' ? (
                    <><span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-bold rounded">A</span><span>Your Age</span></>
                  ) : (
                    <span>Your Age</span>
                  )}
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={getInputValue(inputs.yourAge)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('yourAge', parseInt(value) || 0);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                  onBlur={() => {
                    // Removed intrusive alert — inline message already shown below
                  }}
                  aria-label="Your current age"
                  aria-describedby="age-help"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div id="age-help" className="text-xs text-gray-500 mt-1">
                                          Enter your current age (18-100)
                                        </div>
                                        {inputs.yourAge > 0 && inputs.yourAge < FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE && (
                                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                                <span>⏳</span>
                                                <span>Super accessible in {FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE - inputs.yourAge} years (age {FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE})</span>
                                            </p>
                                        )}
                                        {inputs.yourAge >= FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE && inputs.yourAge < FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE && (
                                            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                                                <span>✓</span>
                                                <span>Super accessible • Pension from age {FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE}</span>
                                            </p>
                                        )}
                                        {inputs.yourAge >= FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE && (
                                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                                <span>✓</span>
                                                <span>Super & Age Pension eligible</span>
                                            </p>
                                        )}
                                        {validationErrors.yourAge && (
                                            <p className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
                                                <span>✕</span>
                                                <span>{validationErrors.yourAge}</span>
                                            </p>
                                        )}
                                        {!validationErrors.yourAge && inputs.yourAge > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Life expectancy ({inputs.yourGender || 'male'}): ~{getLifeExpectancy(inputs.yourAge, inputs.yourGender || 'male').p50} years
                                            </p>
                                        )}
                                    </div>
                                    
              {/* Your Gender */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Gender — For Life Expectancy</label>
                                        <p className="text-xs text-gray-500 mb-2">Used to estimate how long your super needs to last.</p>
                                        <select
                  value={inputs.yourGender || 'male'}
                  onChange={(e) => handleInputChange('yourGender', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                                        </select>
                                    </div>

              {/* Your Super */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  {inputs.relationshipStatus === 'couple' ? (
                    <><span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-bold rounded">A</span><span>Your Super ($)</span></>
                  ) : (
                    <span>Your Super ($)</span>
                  )}
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={getInputValue(inputs.yourSuper)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('yourSuper', parseInt(value) || 0);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                  aria-label="Your current superannuation balance in dollars"
                  aria-describedby="super-help"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {validationErrors.yourSuper && (
                                            <p className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
                                                <span>✕</span>
                                                <span>{validationErrors.yourSuper}</span>
                                            </p>
                                        )}
                                        {!validationErrors.yourSuper && (
                                          <div id="super-help" className="text-xs text-gray-500 mt-1">
                                            Enter your current superannuation balance
                                          </div>
                                        )}
                                    </div>

              {/* Partner's Age - Only show if couple */}
              {inputs.relationshipStatus === 'couple' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-900 text-xs font-bold rounded">B</span>
                    <span>Partner's Age</span>
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                    value={getInputValue(inputs.partnerAge)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange('partnerAge', parseInt(value) || 0);
                    }}
                    onFocus={(e) => {
                      // Select all text on focus for easier editing on mobile
                      e.target.select();
                    }}
                    onBlur={(e) => {
                      // Removed intrusive alert — inline message already shown below field
                    }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        {inputs.partnerAge > 0 && inputs.partnerAge < FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE && (
                                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                                <span>⏳</span>
                                                <span>Super accessible in {FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE - inputs.partnerAge} years (age {FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE})</span>
                                            </p>
                                        )}
                                        {inputs.partnerAge >= FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE && inputs.partnerAge < FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE && (
                                            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                                                <span>✓</span>
                                                <span>Super accessible • Pension from age {FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE}</span>
                                            </p>
                                        )}
                                        {inputs.partnerAge >= FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE && (
                                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                                <span>✓</span>
                                                <span>Super & Age Pension eligible</span>
                                            </p>
                                        )}
                                        {inputs.partnerAge > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Life expectancy ({inputs.partnerGender || 'female'}): ~{getLifeExpectancy(inputs.partnerAge, inputs.partnerGender || 'female').p50} years
                                            </p>
                                        )}
                                    </div>
              )}
                                    
              {/* Partner's Gender - Only show if couple */}
              {inputs.relationshipStatus === 'couple' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                    Partner's Gender — For Life Expectancy</label>
                                        <p className="text-xs text-gray-500 mb-2">Used to estimate how long your partner's super needs to last.</p>
                                        <select
                    value={inputs.partnerGender || 'female'}
                    onChange={(e) => handleInputChange('partnerGender', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                                        </select>
                                    </div>
              )}

              {/* Partner's Super - Only show if couple */}
              {inputs.relationshipStatus === 'couple' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-900 text-xs font-bold rounded">B</span>
                  <span>Partner's Super ($)</span>
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                    value={getInputValue(inputs.partnerSuper)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('partnerSuper', value === '' ? '' : parseInt(value));
                  }}
                    onFocus={(e) => {
                      // Select all text on focus for easier editing on mobile
                      e.target.select();
                    }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
              )}

              {/* Retirement Period */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retirement Period (Years)
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={getInputValue(inputs.retirementPeriod)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('retirementPeriod', parseInt(value) || 0);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${validationErrors.retirementPeriod ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
                                        />
                        {validationErrors.retirementPeriod && (
                          <p className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
                            <span>✕</span>
                            <span>{validationErrors.retirementPeriod}</span>
                          </p>
                        )}
                        {!validationErrors.retirementPeriod && inputs.retirementPeriod > 60 && (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>{inputs.retirementPeriod} years is unusually long. Typical retirement periods are 20-40 years.</span>
                          </p>
                        )}
                        </div>
                        
              {/* Target Annual Income (Optional) */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Annual Income (optional) — How much do you want to withdraw per year? ($)
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={inputs.desiredAnnualIncome === 0 ? 0 : (inputs.desiredAnnualIncome || '')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const val = value === '' ? 0 : parseInt(value);
                    handleInputChange('desiredAnnualIncome', val);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

              {/* Other Taxable Income */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Income (Wages, etc.) — Taxable ($/yr)</label>
                                        <p className="text-xs text-gray-500 mb-2">Income from work or investments that gets taxed. We'll calculate after-tax amount.</p>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={getInputValue(inputs.otherTaxableIncome)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('otherTaxableIncome', value === '' ? '' : parseInt(value));
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

              {/* Other Non-Taxable Income */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Income (Pensions, etc.) — Tax-Free ($/yr)</label>
                                        <p className="text-xs text-gray-500 mb-2">Income that's not taxed (Age Pension, disability support, etc.)</p>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={getInputValue(inputs.otherNonTaxableIncome)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('otherNonTaxableIncome', value === '' ? '' : parseInt(value));
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                        
              {/* Assessable Assets */}
                                    <div>
                <InfoTooltip content="Assets outside of superannuation that are counted for the Age Pension asset test. Includes savings, investments, property (excluding your home), and other assets.">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assessable Assets (ex-super) ($)
                                        </label>
                </InfoTooltip>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={inputs.assessableAssets === 0 ? 0 : (inputs.assessableAssets || '')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('assessableAssets', value === '' ? '' : parseInt(value));
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

              {/* Homeowner Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isHomeowner"
                  checked={inputs.isHomeowner}
                  onChange={(e) => handleInputChange('isHomeowner', e.target.checked)}
                  className="mr-2"
                />
                <InfoTooltip content="Whether you own your home. This affects Age Pension asset test thresholds - homeowners have higher asset limits before pension is reduced.">
                  <label htmlFor="isHomeowner" className="text-sm font-medium text-gray-700">
                    Homeowner
                  </label>
                </InfoTooltip>
                                </div>
                        
              {/* Include Pension Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includePension"
                  checked={inputs.includePension}
                  onChange={(e) => handleInputChange('includePension', e.target.checked)}
                  className="mr-2"
                />
                <InfoTooltip content="Whether to include Australian Age Pension in the retirement income calculations. The pension amount is calculated based on your assets and income using current government rules.">
                  <label htmlFor="includePension" className="text-sm font-medium text-gray-700">
                    Include Pension?
                  </label>
                </InfoTooltip>
                        </div>
                        
              {/* Pension Growth Rate Input */}
              {inputs.includePension && (
                <div className="mt-3">
                  <InfoTooltip content="One annual real (above-CPI) indexation rate for future Age Pension projections. It grows maximum pension rates, part-pension cut-offs, supplements, and rent assistance caps. Asset and income free areas and deeming thresholds stay CPI-flat in today's dollars; deeming percentages stay fixed. Default 0.3% is conservative vs ASIC's separate CPI/wage indexation assumptions.">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pension Growth Rate (%/yr)
                    </label>
                  </InfoTooltip>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={getInputValue(inputs.pensionGrowthRate)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
                      handleInputChange('pensionGrowthRate', value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                            )}
                        </div>
                    </div>
                        
          {/* Center Column */}
                            <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Assumptions</h2>
            
            {/* Assumptions Box */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-bold text-gray-800 mb-2">Assumptions</h3>
              <p className="text-sm text-gray-600 mb-4">Enter a rate or use the presets below.</p>
              
              {/* Assumed Return Input */}
              <div className="mb-4">
                <InfoTooltip content="Expected annual investment return on your superannuation balance. This is the nominal return before inflation. The calculator uses real returns (nominal return minus inflation) for projections.">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assumed Return (%/yr)
                                        </label>
                </InfoTooltip>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.,]?[0-9]*"
                  value={getInputValue(inputs.assumedReturn)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
                    handleInputChange('assumedReturn', value);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                                    </div>

              {/* Assumed Inflation Input */}
              <div className="mb-4">
                <InfoTooltip content="Expected annual inflation rate. This is used to calculate real returns and ensure all projections are in today's purchasing power. All amounts shown are in real terms (inflation-adjusted).">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assumed Inflation (%/yr)
                                        </label>
                </InfoTooltip>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="[0-9]*[.,]?[0-9]*"
                  value={getInputValue(inputs.assumedInflation)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
                    handleInputChange('assumedInflation', value);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

              {/* Preset Buttons */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleInputChange('assumedReturn', 4.5)}
                  className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  Low
                </button>
                <button
                  onClick={() => handleInputChange('assumedReturn', 6.5)}
                  className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  Medium
                </button>
                <button
                  onClick={() => handleInputChange('assumedReturn', 8.5)}
                  className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  High
                </button>
              </div>
            </div>

            {/* Fees Box */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Fees</h3>
              
              {/* Admin Fees */}
              <div className="mb-4">
                <InfoTooltip content="AustralianSuper flat administration fee">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fixed Admin Fee ($/year)
                                            </label>
                </InfoTooltip>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                  value={getInputValue(inputs.adminFees)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('adminFees', parseInt(value) || 0);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">AustralianSuper: $52 flat admin fee</p>
                                        </div>

              {/* Admin Fee Percentage */}
              <div className="mb-4">
                <InfoTooltip content="AustralianSuper admin fee percentage">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Fee (%/year)
                                            </label>
                </InfoTooltip>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                pattern="[0-9]*[.,]?[0-9]*"
                  value={getInputValue(inputs.adminFeePercentage)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
                    handleInputChange('adminFeePercentage', value);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">AustralianSuper: 0.10% of balance (capped at $350 when using defaults)</p>
                                        </div>

              {/* Investment Fees */}
              <div className="mb-4">
                <InfoTooltip content="Investment management fees charged as a percentage of your account balance. These fees cover the cost of managing your investments and typically range from 0.5% to 2.0% per year. Higher fees can significantly impact your long-term returns.">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                    Investment Fees (%/yr)
                                        </label>
                </InfoTooltip>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            pattern="[0-9]*[.,]?[0-9]*"
                  value={getInputValue(inputs.investmentFees)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
                    handleInputChange('investmentFees', value);
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

              {/* Insurance Fees */}
                                    <div>
                <InfoTooltip content="Annual insurance premiums for life insurance, total and permanent disability (TPD), and income protection insurance within your superannuation. These are typically fixed dollar amounts. You can opt out of insurance to reduce fees, but this reduces your protection.">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insurance Fees ($/yr)
                                        </label>
                </InfoTooltip>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                  value={getInputValue(inputs.insuranceFees)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('insuranceFees', value === '' ? '' : parseInt(value));
                  }}
                  onFocus={(e) => {
                    // Select all text on focus for easier editing on mobile
                    e.target.select();
                  }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

            {/* Run button - desktop (lg): sticky so it stays visible when scrolling */}
            <div className="mt-6 hidden lg:block sticky bottom-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] py-4 -mx-2 px-2 sm:mx-0 sm:px-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <p className="text-xs font-semibold text-gray-600">Return assumption:</p>
                {[
                  { key: 'low',    label: 'Low',    sublabel: '4.5%' },
                  { key: 'medium', label: 'Medium', sublabel: '6.5%' },
                  { key: 'high',   label: 'High',   sublabel: '8.5%' },
                ].map((r) => (
                  <button key={r.key} type="button" onClick={() => applyReturnPreset(r.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition text-left ${selectedReturnPreset === r.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'}`}>
                    <span className="font-medium">{r.label}</span>
                    <span className={`text-xs ml-1 ${selectedReturnPreset === r.key ? 'text-teal-100' : 'text-gray-500'}`}>{r.sublabel}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-600 font-medium mb-2">
                {selectedScenarioPreset ? 'Example loaded — tap Run or edit any field for your own numbers.' : 'Pick an example scenario above or fill in your own details.'}
              </p>
              <button
                ref={runButtonRef}
                type="button"
                onClick={() => {
                  setIsCalculating(true);
                  runProjection();
                  setTimeout(() => setIsCalculating(false), 150);
                }}
                disabled={isCalculating}
                className="w-full py-3.5 px-5 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white font-bold text-base rounded-lg shadow-lg ring-2 ring-blue-500/30 transition-colors"
                aria-label="Run retirement projection"
              >
                {isCalculating ? 'Calculating…' : (
                  window._abAssignments?.cta_copy === 'variant'
                    ? 'See How Long My Super Lasts →'
                    : 'Run My First Stress Test (30 sec)'
                )}
              </button>
            </div>

            {/* Primary handoff lives in the results column — no competing delayed upsell here. */}
                        </div>

          {/* Right Column */}
                            <div ref={projectionResultsRef} className={`${hasRunProjection && chartData.length > 0 ? 'bg-gradient-to-br from-blue-50 to-green-50 border-2 border-blue-300 ring-2 ring-blue-200' : 'bg-white'} p-6 rounded-lg shadow-lg transition-all duration-300`}>
            <h2 className={`text-2xl font-bold mb-1 ${hasRunProjection && chartData.length > 0 ? 'text-blue-900' : 'text-gray-800'}`}>Your Retirement Plan</h2>
            {hasRunProjection && chartData.length > 0 && (
              <>
                <p className="text-sm text-green-700 mb-2 font-semibold">✓ Projection complete</p>
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                  <p className="text-lg font-bold text-green-900 mb-1">
                    You can sustainably withdraw <span className="text-2xl text-green-600">
                      ${(chartData[0]?.totalIncome ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span> per year
                  </p>
                  <p className="text-xs text-green-700 mt-2">For {inputs.retirementPeriod} years in today's dollars (based on your assumptions)</p>
                  <p className="text-xs text-green-800 mt-2 opacity-90">Same amount each year your super can sustain for your full retirement period. Includes Age Pension and fees; amounts in today's purchasing power.</p>
                </div>
                <p className="text-xs text-gray-500 mb-4">{SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) ? 'Your inputs stay on this page. Saved automatically — subscriber benefit.' : 'Inputs reset after each run. Subscribers keep their inputs — subscribe to save and restore.'}</p>

                <div className="mb-4 rounded-xl border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-indigo-50 p-4 shadow-sm">
                  <p className="text-sm font-bold text-purple-900 mb-2">Next step: continue in the retirement calculator</p>
                  <p className="text-xs text-purple-800 mb-3">Your age, super, income target, pension settings, and fees will transfer. Stress-test the same plan against 98 years of market data — 1 free Advanced run, no card required.</p>
                  <button
                    type="button"
                    onClick={() => CalculatorBridge.navigateBasicToAdvanced(getInputsForAdvancedTransfer(), { eventLabel: 'Basic results primary CTA' })}
                    className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-bold text-base rounded-lg shadow-md transition-colors"
                  >
                    Continue in the retirement calculator →
                  </button>
                  {!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
                    <p className="text-xs text-purple-800 mt-3 mb-0">
                      <button
                        type="button"
                        onClick={() => openPricingModal({ source: 'basic_results_primary', calculatorTier: 'basic' })}
                        className="underline font-semibold text-purple-900 hover:text-purple-700"
                      >
                        Save inputs / unlimited runs later — see pricing
                      </button>
                      <span className="text-purple-700"> · Basic PDF summary is free by email below.</span>
                    </p>
                  )}
                  {SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
                    <p className="text-xs text-purple-700 mt-2 mb-0">Basic PDF summary is free by email below.</p>
                  )}
                  <AdvancedWalkthroughVideo
                    placement="basic_results_primary"
                    calculatorTier="basic"
                    compact
                  />
                </div>

                {/* Insight copy only — primary handoff is the block above */}
                {(() => {
                  const msi = chartData[0]?.totalIncome ?? 0;
                  const desired = lastRunDesiredIncome || 0;
                  const finalBal = chartData[chartData.length - 1]?.endingBalance ?? 0;
                  const shortfall = desired > 0 && msi > 0 && desired > msi;
                  const surplus = desired > 0 && msi >= desired;
                  const borderColor = shortfall ? 'border-amber-400' : 'border-blue-300';
                  const bgColor = shortfall ? 'from-amber-50 to-orange-50' : 'from-blue-50 to-indigo-50';
                  return (
                    <div className={`rounded-xl border-2 ${borderColor} bg-gradient-to-r ${bgColor} p-4 mb-4`}>
                      <p className="text-sm font-bold text-gray-900 mb-1">
                        {shortfall
                          ? `⚠ Gap detected — you want $${Math.round(desired/1000)}k/yr but can sustainably withdraw $${Math.round(msi/1000)}k/yr`
                          : surplus
                            ? `✓ On track — your super can sustain your $${Math.round(desired/1000)}k/yr target`
                            : `✓ Projection complete — max sustainable income $${Math.round(msi/1000)}k/yr`
                        }
                      </p>
                      <p className="text-xs text-gray-600 mb-0">
                        {shortfall
                          ? `This projection uses a single assumed return rate. Real markets vary — continue in the retirement calculator above to stress-test with 98 years of historical data.`
                          : finalBal < msi
                            ? `Final balance: $${Math.round(finalBal/1000)}k. These are constant-return estimates — use the continue button above to test against real historical sequences.`
                            : `This uses a single constant return rate. Use the continue button above to test against 98 years of real market history, including crashes.`
                        }
                      </p>
                    </div>
                  );
                })()}

                {/* Email Capture - After insight (desktop Basic) */}
                <div className="mt-6" aria-label="Email your results">
                  <section className="bg-white rounded-2xl border-2 border-blue-200 shadow-xl shadow-blue-100/50 overflow-hidden p-6 sm:p-8">
                    <EmailCaptureForm
                      calculatorType="basic"
                      results={{
                        ...buildBasicEmailResults(chartData, {
                          successRate: 100,
                          periodLength: getInputsForAdvancedTransfer().retirementPeriod,
                          startYear: new Date().getFullYear()
                        }),
                        chartData
                      }}
                      inputs={{
                        ...getInputsForAdvancedTransfer(),
                        periodLength: getInputsForAdvancedTransfer().retirementPeriod
                      }}
                      getPdfBase64={() =>
                        generateBasicCalculatorPdfBase64(
                          getInputsForAdvancedTransfer(),
                          chartData,
                          whatIfResults
                        )
                      }
                    />
                  </section>
                </div>
              </>
            )}
            {validationErrors.calculationError && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-semibold">⚠️ Error:</p>
                <p className="text-sm text-red-700 mt-1">{validationErrors.calculationError}</p>
              </div>
            )}
            {hasRunProjection && chartData.length > 0 && inputs.relationshipStatus === 'couple' && Math.abs((inputs.yourAge || 0) - (inputs.partnerAge || 0)) > 2 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-800 font-semibold">⚠️ Note:</p>
                <p className="text-xs text-amber-700 mt-1">This projection assumes both partners retire at the same time. If you plan to retire at different ages, the result will differ.</p>
              </div>
            )}
            <hr className={`mb-6 ${hasRunProjection && chartData.length > 0 ? 'border-blue-200' : 'border-gray-300'}`} />
            
            {/* Final Balance Box */}
            <div className={`${hasRunProjection && chartData.length > 0 ? 'bg-white border-2 border-green-300 ring-1 ring-green-200' : 'bg-gray-50 border border-gray-200'} p-4 rounded-lg mb-4 transition-all`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Final Balance (in today's dollars)</h3>
              <p className="text-xs text-gray-600 mb-2">Remaining super at the end of the period after withdrawals, returns, and fees.</p>
              <p className={`font-bold ${hasRunProjection && chartData.length > 0 ? 'text-3xl text-blue-600' : 'text-base text-gray-500 italic'}`}>
                {hasRunProjection && chartData.length > 0 
                  ? `$${chartData[chartData.length - 1].endingBalance.toLocaleString()}` 
                  : 'Run calculation to see results'}
              </p>
            </div>

            {/* Warning if desired income is much higher than MSI */}
            {hasRunProjection && chartData.length > 0 && lastRunDesiredIncome > 0 && (() => {
              const maxSustainableIncome = chartData[0]?.maxSustainableIncome ?? chartData[0]?.totalIncome ?? 0;
              const desiredIncome = lastRunDesiredIncome;
              // Only show warning if MSI is > 0 and desired income is significantly higher
              if (maxSustainableIncome > 0 && desiredIncome > maxSustainableIncome * 1.2) {
                return (
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-4">
                    <p className="text-sm text-red-800 font-semibold mb-2">
                      ⚠️ Your desired income ($<strong>{Math.round(desiredIncome).toLocaleString()}</strong>/year) is significantly higher than what you can sustainably withdraw ($<strong>{Math.round(maxSustainableIncome).toLocaleString()}</strong>/year).
                    </p>
                    <p className="text-sm text-red-700 mb-2">
                      This means your super balance will <strong>run out before the end of your retirement period</strong> if you try to withdraw $<strong>{Math.round(desiredIncome).toLocaleString()}</strong> every year.
                    </p>
                    <p className="text-sm text-red-700">
                      To achieve your desired income, you would need to:
                    </p>
                    <ul className="text-sm text-red-700 list-disc list-inside mt-2 ml-2">
                      <li>Increase your super balance before retirement</li>
                      <li>Reduce your retirement period</li>
                      <li>Increase your investment returns (higher risk)</li>
                      <li>Reduce fees</li>
                      <li>Accept a lower income in retirement</li>
                    </ul>
                    <p className="text-sm text-red-700 mt-2 font-semibold">
                      Check the "Balance with Target Income" line in the chart below - if it reaches $0 before the end of your retirement period, your plan is not sustainable.
                    </p>
                  </div>
                );
              }
              return null;
            })()}
                        
            {/* Per-Partner Eligibility Timeline - For Couples */}
            {hasRunProjection && chartData.length > 0 && inputs.relationshipStatus === 'couple' && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>👥</span>
                  <span>Per-Partner Eligibility Status</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* You / Partner A */}
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2 text-sm">You (Partner A)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Age:</span>
                        <span className="font-bold text-blue-600">{inputs.yourAge}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Gender:</span>
                        <span className="font-medium">{inputs.yourGender || 'male'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Super:</span>
                        <span className="font-bold">${(inputs.yourSuper || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Life Exp:</span>
                        <span className="text-xs">{getLifeExpectancy(inputs.yourAge, inputs.yourGender || 'male').p50} yrs (median)</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          {inputs.yourAge >= FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Super Access: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Super Access: Year {FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE - inputs.yourAge}</span></>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {inputs.yourAge >= FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Age Pension: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Age Pension: Year {FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE - inputs.yourAge}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Partner / Partner B */}
                  <div className="bg-white p-3 rounded-lg border border-purple-100">
                    <h4 className="font-semibold text-purple-900 mb-2 text-sm">Partner (Partner B)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Age:</span>
                        <span className="font-bold text-purple-600">{inputs.partnerAge || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Gender:</span>
                        <span className="font-medium">{inputs.partnerGender || 'female'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Super:</span>
                        <span className="font-bold">${(inputs.partnerSuper || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Life Exp:</span>
                        <span className="text-xs">{getLifeExpectancy(inputs.partnerAge || 67, inputs.partnerGender || 'female').p50} yrs (median)</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          {(inputs.partnerAge || 0) >= 60 ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Super Access: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Super Access: Year {60 - (inputs.partnerAge || 0)}</span></>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {(inputs.partnerAge || 0) >= 67 ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Age Pension: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Age Pension: Year {67 - (inputs.partnerAge || 0)}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Combined Eligibility Summary */}
                <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2">
                    <span>🔄</span>
                    <span>Combined Eligibility</span>
                  </h4>
                  <div className="space-y-1 text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      {inputs.yourAge >= 60 && (inputs.partnerAge || 0) >= 60 ? (
                        <><span className="text-green-500 font-bold">✓</span><span><strong>Super Withdrawals:</strong> Both partners eligible - withdrawals active</span></>
                      ) : (
                        <><span className="text-amber-500 font-bold">⏳</span><span><strong>Super Withdrawals:</strong> Start in year {Math.max(60 - inputs.yourAge, 60 - (inputs.partnerAge || 0))} when youngest partner reaches 60</span></>
                      )}
                    </div>
                    {inputs.includePension && (
                      <div className="flex items-center gap-2">
                        {inputs.yourAge >= 67 && (inputs.partnerAge || 0) >= 67 ? (
                          <><span className="text-green-500 font-bold">✓</span><span><strong>Couple Age Pension:</strong> Both partners eligible - pension active</span></>
                        ) : (
                          <><span className="text-amber-500 font-bold">⏳</span><span><strong>Couple Age Pension:</strong> Start in year {Math.max(67 - inputs.yourAge, 67 - (inputs.partnerAge || 0))} when youngest partner reaches 67</span></>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Visual Timeline */}
                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm flex items-center gap-2">
                    <span>📅</span>
                    <span>Retirement Milestones Timeline</span>
                  </h4>
                  
                  {/* Timeline for Partner A */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-blue-900 mb-2">You (Partner A)</p>
                    <div className="relative h-10 bg-gray-100 rounded-lg overflow-hidden">
                      {/* Preservation Age (60) marker */}
                      {inputs.yourAge < 60 && (
                        <div className="absolute top-0 bottom-0 bg-amber-200 border-r-2 border-amber-500" 
                             style={{left: '0%', width: `${Math.min(100, ((60 - inputs.yourAge) / inputs.retirementPeriod) * 100)}%`}}>
                          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-bold text-amber-800 whitespace-nowrap">
                            Age 60 ↓
                          </span>
                        </div>
                      )}
                      {/* Pension Age (67) marker */}
                      {inputs.yourAge < 67 && (
                        <div className="absolute top-0 bottom-0 border-r-2 border-blue-500" 
                             style={{left: `${Math.min(100, ((67 - inputs.yourAge) / inputs.retirementPeriod) * 100)}%`}}>
                          <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-800 whitespace-nowrap">
                            ↓ Age 67
                          </span>
                        </div>
                      )}
                      {/* Current status overlay - positioned to avoid overlap with markers */}
                      <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center z-10">
                        <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                          {inputs.yourAge >= 67 ? '✓ Fully Eligible' : inputs.yourAge >= 60 ? '✓ Super Only' : `${60 - inputs.yourAge}yr to Super`}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Timeline for Partner B */}
                  <div>
                    <p className="text-xs font-medium text-purple-900 mb-2">Partner (Partner B)</p>
                    <div className="relative h-10 bg-gray-100 rounded-lg overflow-hidden">
                      {/* Preservation Age (60) marker */}
                      {(inputs.partnerAge || 0) < 60 && (
                        <div className="absolute top-0 bottom-0 bg-amber-200 border-r-2 border-amber-500" 
                             style={{left: '0%', width: `${Math.min(100, ((60 - (inputs.partnerAge || 0)) / inputs.retirementPeriod) * 100)}%`}}>
                          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-bold text-amber-800 whitespace-nowrap">
                            Age 60 ↓
                          </span>
                        </div>
                      )}
                      {/* Pension Age (67) marker */}
                      {(inputs.partnerAge || 0) < 67 && (
                        <div className="absolute top-0 bottom-0 border-r-2 border-purple-500" 
                             style={{left: `${Math.min(100, ((67 - (inputs.partnerAge || 0)) / inputs.retirementPeriod) * 100)}%`}}>
                          <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[10px] font-bold text-purple-800 whitespace-nowrap">
                            ↓ Age 67
                          </span>
                        </div>
                      )}
                      {/* Current status overlay - positioned to avoid overlap with markers */}
                      <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center z-10">
                        <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                          {(inputs.partnerAge || 0) >= 67 ? '✓ Fully Eligible' : (inputs.partnerAge || 0) >= 60 ? '✓ Super Only' : `${60 - (inputs.partnerAge || 0)}yr to Super`}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-3 pt-3 border-t border-gray-200 flex gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-amber-200 border border-amber-500 rounded"></div>
                      <span className="text-gray-600">Waiting Period</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
                      <span className="text-gray-600">Eligible Period</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Additional Results - Success Rate and Age Pension */}
            {hasRunProjection && chartData.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Plan Success - Check if can achieve desired income */}
                <div className={`p-4 rounded-lg border ${lastRunDesiredIncome > 0 && (chartData[0]?.maxSustainableIncome ?? chartData[0]?.totalIncome ?? 0) < lastRunDesiredIncome ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`}>
                  {(() => {
                    const maxSustainableIncome = chartData[0]?.maxSustainableIncome ?? chartData[0]?.totalIncome ?? 0;
                    const desiredIncome = lastRunDesiredIncome;
                    const shortfall = desiredIncome - maxSustainableIncome;
                    const canAchieve = desiredIncome === 0 || maxSustainableIncome >= desiredIncome;
                    
                    if (desiredIncome > 0 && !canAchieve) {
                      return (
                        <>
                          <h4 className="text-sm font-semibold text-red-800 mb-2">⚠️ Income Shortfall</h4>
                          <p className="text-sm text-red-900 font-semibold mb-2">
                            You can sustainably withdraw <span className="text-lg text-red-700">${Math.round(maxSustainableIncome).toLocaleString()}/year</span>
                          </p>
                          <p className="text-sm text-red-800 mb-2">
                            But you wanted <span className="font-bold">${Math.round(desiredIncome).toLocaleString()}/year</span>
                          </p>
                          <p className="text-lg font-bold text-red-700 mb-2">
                            Shortfall: ${Math.round(shortfall).toLocaleString()}/year
                          </p>
                          <p className="text-xs text-red-600 mt-2">
                            You'd need to increase super, work longer, or reduce your desired income.
                          </p>
                        </>
                      );
                    }
                    
                    return (
                      <>
                        <h4 className="text-sm font-semibold text-blue-800 mb-1">Plan Succeeds</h4>
                        <p className="text-2xl font-bold text-blue-900">✓</p>
                        <p className="text-xs text-blue-600 mt-1">
                          {desiredIncome > 0 
                            ? 'You can achieve your desired income' 
                            : 'Your super balance lasts'}
                        </p>
                        <p className="caveat-basic-note text-sm mt-2 font-medium leading-snug" style={{ minHeight: '2.5em' }}>
                          Note: Based purely on your inputs — return and inflation are constant and don&apos;t reflect real-life market variation. The Advanced calculator lets you test your plan against real-world results.
                        </p>
                      </>
                    );
                  })()}
                </div>
                
                {/* Age Pension Estimate */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-sm font-semibold text-green-800 mb-1">Age Pension Estimate</h4>
                  <p className="text-2xl font-bold text-green-900">
                    ${Math.round(chartData[0]?.pension || 0).toLocaleString()}/yr
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {inputs.includePension ? 'Initial year estimate' : 'Not included'}
                  </p>
                </div>
              </div>
            )}
            
            {/* Age Pension Entitlement Section */}
            {hasRunProjection && inputs.includePension && chartData.length > 0 && chartData[0]?.pension > 0 && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-6">
                <h3 className="text-lg font-semibold text-green-800 mb-2">Age Pension Entitlement</h3>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Initial Pension:</strong> ${Math.round(chartData[0].pension).toLocaleString()} per year
                </p>
                <p className="text-xs text-gray-600">
                  The Age Pension is calculated based on both an assets test and an income test. 
                  You receive the lower of the two amounts. The pension increases as your super balance 
                  decreases and adjusts for inflation over time.
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  <strong>Note:</strong> Pension calculations assume you're age 67+ and meet residency requirements.
                </p>
              </div>
            )}
                        
            {/* Super Balance Line Chart */}
            {hasRunProjection && (
              <div className="mb-6" id="basic-balance-chart">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Super Balance Over Time (in today's dollars)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'balance') return [`$${value.toLocaleString()}`, lastRunDesiredIncome > 0 ? 'Balance with MSI' : 'Super Balance'];
                        if (name === 'balanceWithDesiredIncome') return [`$${value.toLocaleString()}`, 'Balance with Target Income'];
                        return [`$${value.toLocaleString()}`, name];
                      }}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    {lastRunDesiredIncome > 0 && (
                      <Legend 
                        verticalAlign="top" 
                        height={36}
                        formatter={(value) => {
                          if (value === 'balance') return 'Balance with MSI (sustainable)';
                          if (value === 'balanceWithDesiredIncome') return 'Balance with Target Income';
                          return value;
                        }}
                      />
                    )}
                    <Area 
                      type="monotone" 
                      dataKey="balance" 
                      name="balance"
                      stroke="#3B82F6" 
                      fill="#3B82F6" 
                      fillOpacity={0.3}
                    />
                    {lastRunDesiredIncome > 0 && (
                      <Area 
                        type="monotone" 
                        dataKey="balanceWithDesiredIncome" 
                        name="balanceWithDesiredIncome"
                        stroke="#EF4444" 
                        fill="#EF4444" 
                        fillOpacity={0.2}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
                    </div>
                )}
                
            {/* Annual Income Bar Chart - Maximum Sustainable Income */}
            {hasRunProjection && (
                                    <div id="basic-income-chart-msi">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Annual Income - Maximum Sustainable Income (today's dollars)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="otherTaxableIncome" stackId="a" fill="#F59E0B" name="Other Taxable Income (After Tax)" />
                    <Bar dataKey="otherNonTaxableIncome" stackId="a" fill="#8B5CF6" name="Other Non-Taxable Income" />
                    <Bar dataKey="withdrawalFromSuper" stackId="a" fill="#3B82F6" name="Withdrawal from Super" />
                    <Bar dataKey="pension" stackId="a" fill="#10B981" name="Pension" />
                    {/* Show average total income across all years */}
                    {(() => {
                      const rowTotal = (row) => Number.isFinite(row.totalIncome)
                        ? row.totalIncome
                        : (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.withdrawalFromSuper || 0) + (row.pension || 0);
                      const allIncomes = chartData.map(rowTotal);
                      if (allIncomes.length === 0) return null;
                      const avgIncomeAllYears = allIncomes.reduce((sum, inc) => sum + inc, 0) / allIncomes.length;
                      return (
                        <Line 
                          type="monotone" 
                          dataKey={() => avgIncomeAllYears}
                          stroke="#DC2626" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          name={`Average income (${allIncomes.length} yrs)`}
                        />
                      );
                    })()}
                  </ComposedChart>
                </ResponsiveContainer>
                {chartData.length > 0 && (() => {
                  const rowTotal = (row) => Number.isFinite(row.totalIncome)
                    ? row.totalIncome
                    : (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.withdrawalFromSuper || 0) + (row.pension || 0);
                  const allIncomes = chartData.map(rowTotal);
                  const avgIncomeAllYears = allIncomes.reduce((sum, inc) => sum + inc, 0) / allIncomes.length;
                  return (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4 rounded">
                      <p className="text-sm text-gray-800 font-semibold mb-2">
                        ℹ️ Fixed Income Calculation
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        The <strong>Maximum Sustainable Income (MSI)</strong> shown above is calculated as a <strong>true fixed income</strong> - the same amount every year that your super can sustain for the entire retirement period.
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Important:</strong> Australian superannuation law requires <strong>minimum annual withdrawals</strong> based on your age (5% at 65-74, 6% at 75-79, 7% at 80-84, 9% at 85-89). In reality, if your super balance grows, these minimums may force you to withdraw <strong>more than the sustainable amount</strong> in some years. The red dotted line shows the <strong>average total income across all {allIncomes.length} years</strong> (${Math.round(avgIncomeAllYears).toLocaleString()}/year), including those higher-withdrawal years.
                      </p>
                      <p className="text-sm text-gray-700">
                        The sustainable income above is what you can take every year in theory. To see the impact of minimum withdrawal requirements, check the "What-If Scenarios" section below.
                      </p>
                    </div>
                  );
                })()}
                                    </div>
            )}
            
            {/* Annual Income Bar Chart - Target Income */}
            {hasRunProjection && lastRunDesiredIncome > 0 && (
                                    <div id="basic-income-chart-target" className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Annual Income - Your Target Income (today's dollars)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="otherTaxableIncome" stackId="b" fill="#F59E0B" name="Other Taxable Income (After Tax)" />
                    <Bar dataKey="otherNonTaxableIncome" stackId="b" fill="#8B5CF6" name="Other Non-Taxable Income" />
                    <Bar dataKey="withdrawalFromSuperDesired" stackId="b" fill="#EF4444" name="Withdrawal from Super" />
                    <Bar dataKey="pensionDesired" stackId="b" fill="#10B981" name="Pension" />
                  </BarChart>
                </ResponsiveContainer>
                                    </div>
            )}
            
            {/* Scroll Down Message */}
            {hasRunProjection && chartData.length > 0 && (
              <div className="mt-6 text-center">
                <h3 className="text-lg font-bold text-gray-800">Scroll down for Year-by-Year Table and What-If Scenarios</h3>
              </div>
            )}

            {/* Related Reading - Health Care Costs Blog */}
            {hasRunProjection && chartData.length > 0 && (
              <div className="mt-8 max-w-2xl mx-auto" aria-label="Related reading">
                <section className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl border border-slate-200 shadow-md p-6 sm:p-8">
                  <h3 className="text-lg font-bold text-slate-800 mb-3">Planning for Health Costs in Retirement?</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Don't overlook one of retirement's biggest wildcards. Learn how health status affects lifetime costs, what Australian retirees actually spend on medical care, and how to stress-test your plan for aged care.
                  </p>
                  <a href="/blog/health-care-costs-retirement-2026.html" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors">
                    <i className="fas fa-book-open"></i>
                    Read: Health Care Costs in Retirement
                  </a>
                </section>
              </div>
            )}
            </div>
        </div>
        
        {/* Run Projection Button - Full Width (mobile); sticky so it stays visible when scrolling */}
        <div className="mt-6 lg:hidden sticky bottom-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] py-4 -mx-2 px-2 sm:mx-0 sm:px-0">
          <div className="mb-2 lg:hidden">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <p className="text-xs font-semibold text-gray-600">Return:</p>
              {[
                { key: 'low',    label: 'Low 4.5%' },
                { key: 'medium', label: 'Med 6.5%' },
                { key: 'high',   label: 'High 8.5%' },
              ].map((r) => (
                <button key={r.key} type="button" onClick={() => applyReturnPreset(r.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${selectedReturnPreset === r.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-600 font-medium mb-2">
              {selectedScenarioPreset ? 'Example loaded — tap Run or edit any field.' : 'Pick an example above or fill in your own details.'}
            </p>
          </div>
          <MobileButton
            onClick={async () => {
              setIsCalculating(true);
              triggerHaptic('medium');
              
              // Wait for spinner to render (double RAF + timeout)
              await new Promise(resolve => {
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    setTimeout(resolve, 100);
                  });
                });
              });
              
              runProjection();
              setIsCalculating(false);
              triggerHaptic('success');
            }}
            variant="primary"
            size="large"
            fullWidth={true}
            aria-label="Calculate retirement projection based on current inputs"
          >
            {window._abAssignments?.cta_copy === 'variant' ? 'See How Long My Super Lasts →' : 'Run My First Stress Test (30 sec)'}
          </MobileButton>
        </div>
        
        {/* Eligibility Status Banner - Shows for mixed-age couples or those under 60/67 */}
        {hasRunProjection && (
          <>
            {(inputs.yourAge < 60 || (inputs.relationshipStatus === 'couple' && inputs.partnerAge < 60)) && (
              <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⏳</span>
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-1">Retirement Timeline</h4>
                    <p className="text-sm text-amber-800">
                      {inputs.relationshipStatus === 'couple' && inputs.partnerAge < 60 && inputs.yourAge >= 60 ? (
                        <>Your partner will reach preservation age ({FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE}) in <strong>{FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE - inputs.partnerAge} years</strong>. Superannuation withdrawals for your partner will begin at that time.</>
                      ) : inputs.relationshipStatus === 'couple' && inputs.yourAge < 60 && inputs.partnerAge >= 60 ? (
                        <>You will reach preservation age (60) in <strong>{60 - inputs.yourAge} years</strong>. Your superannuation withdrawals will begin at that time.</>
                      ) : inputs.relationshipStatus === 'couple' && inputs.yourAge < 60 && inputs.partnerAge < 60 ? (
                        <>Both partners will reach preservation age (60) in <strong>{Math.max(60 - inputs.yourAge, 60 - inputs.partnerAge)} years</strong>. Super withdrawals will begin when the youngest partner reaches 60.</>
                      ) : (
                        <>You will reach preservation age (60) in <strong>{60 - inputs.yourAge} years</strong>. Superannuation withdrawals will begin at that time.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {inputs.includePension && inputs.relationshipStatus === 'couple' && (inputs.yourAge < 67 || inputs.partnerAge < 67) && (
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Age Pension Eligibility (Couples)</h4>
                    <p className="text-sm text-blue-800">
                      {inputs.yourAge >= 67 && inputs.partnerAge < 67 ? (
                        <>Your partner will reach Age Pension age (67) in <strong>{67 - inputs.partnerAge} years</strong>. Couple Age Pension will begin when both partners are eligible.</>
                      ) : inputs.partnerAge >= 67 && inputs.yourAge < 67 ? (
                        <>You will reach Age Pension age (67) in <strong>{67 - inputs.yourAge} years</strong>. Couple Age Pension will begin when both partners are eligible.</>
                      ) : (
                        <>Both partners will reach Age Pension age (67) in <strong>{Math.max(67 - inputs.yourAge, 67 - inputs.partnerAge)} years</strong>. Couple pension requires both partners to be 67+.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Year-by-Year Table - Full Width */}
        {hasRunProjection && chartData.length > 0 && (
          <div className="mt-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Year-by-Year Breakdown</h3>
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left border-b">Year</th>
                    {inputs.relationshipStatus === 'couple' ? (
                      <>
                        <th className="p-2 text-left border-b bg-blue-50 text-blue-900">Age (A)</th>
                        <th className="p-2 text-left border-b bg-purple-50 text-purple-900">Age (B)</th>
                      </>
                    ) : (
                      <th className="p-2 text-left border-b">Age</th>
                    )}
                    <th className="p-2 text-right border-b">Super Balance</th>
                    <th className="p-2 text-right border-b">Super Withdrawal</th>
                    <th className="p-2 text-right border-b">Age Pension</th>
                    <th className="p-2 text-right border-b">Other Income</th>
                    <th className="p-2 text-right border-b">Total Income</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 border-b">{row.year}</td>
                      {inputs.relationshipStatus === 'couple' ? (
                        <>
                          <td className="p-2 border-b bg-blue-50 text-blue-900 font-medium">{inputs.yourAge + idx}</td>
                          <td className="p-2 border-b bg-purple-50 text-purple-900 font-medium">{(inputs.partnerAge || 0) + idx}</td>
                        </>
                      ) : (
                        <td className="p-2 border-b">{inputs.yourAge + idx}</td>
                      )}
                      <td className="p-2 text-right border-b">${row.balance.toLocaleString()}</td>
                      <td className="p-2 text-right border-b">${row.withdrawal.toLocaleString()}</td>
                      <td className="p-2 text-right border-b">${row.pension.toLocaleString()}</td>
                      <td className="p-2 text-right border-b">${row.otherIncome.toLocaleString()}</td>
                      <td className="p-2 text-right border-b font-semibold">${row.totalIncome.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* What-If Scenarios - Full Width (Essential+); visible for all, inputs/buttons disabled for free */}
        {hasRunProjection && (() => {
          const whatIfUnlocked = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.BASIC_WHATIFS);
          return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">What-If Scenarios</h3>
            {!whatIfUnlocked && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm text-amber-800">Unlock with Advanced Retirement to run these scenarios.</span>
                <button type="button" onClick={() => openPricingModal({ source: 'basic_whatifs', calculatorTier: 'basic' })} className="px-3 py-1.5 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors">Upgrade</button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
                  {/* Live to Age */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">Live to Age?</h4>
                    <p className="text-xs text-gray-600 mb-2">Calculate the maximum sustainable income if you live to a specific age. Your super will be depleted by that age.</p>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                      value={getInputValue(inputs.whatIfLiveToAge)}
                      onChange={(e) => {
                        if (!whatIfUnlocked) return;
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleInputChange('whatIfLiveToAge', parseInt(value) || 0);
                      }}
                      onFocus={(e) => {
                        if (!whatIfUnlocked) return;
                        e.target.select();
                      }}
                      disabled={!whatIfUnlocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100"
                      placeholder="Enter target age (e.g., 100)"
                    />
                    <button
                      type="button"
                      onClick={runLiveToAgeWhatIf}
                      disabled={!whatIfUnlocked}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-500"
                    >
                      Run
                    </button>
                    {whatIfUnlocked && whatIfResults.liveToAge && (
                      <div className="mt-3 text-sm">
                        <div className="text-gray-700 mb-2">
                          <strong>Maximum sustainable income:</strong> ${whatIfResults.liveToAge.avgIncome.toLocaleString()}/yr
                        </div>
                        <div className="text-gray-700 mb-2">
                          <strong>Final balance at age {inputs.whatIfLiveToAge}:</strong> ${whatIfResults.liveToAge.finalBalance.toLocaleString()}
                        </div>
                        <div className="text-gray-700 mb-2">
                          <strong>Retirement period:</strong> {whatIfResults.liveToAge.survivalYears} years
                        </div>
                        <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <strong>Explanation:</strong> This shows the maximum income you can withdraw each year if you want your super to last exactly until age {inputs.whatIfLiveToAge}. The final balance should be close to $0.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Need More Income */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">Need More Income</h4>
                    <p className="text-xs text-gray-600 mb-2">See the impact of needing additional annual income on your super balance.</p>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                      value={getInputValue(inputs.whatIfMoreIncome)}
                      onChange={(e) => {
                        if (!whatIfUnlocked) return;
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleInputChange('whatIfMoreIncome', parseFloat(value) || 0);
                      }}
                      onFocus={(e) => { if (whatIfUnlocked) e.target.select(); }}
                      disabled={!whatIfUnlocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                    <button
                      type="button"
                      onClick={runMoreIncomeWhatIf}
                      disabled={!whatIfUnlocked}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-500"
                    >
                      Run
                    </button>
                    {whatIfUnlocked && whatIfResults.moreIncome && (
                      <div className="mt-3 text-sm">
                        <div className={`font-semibold mb-2 ${whatIfResults.moreIncome.planSucceeds ? 'text-green-700' : 'text-red-700'}`}>
                          {whatIfResults.moreIncome.planSucceeds ? '✅ Plan Succeeds' : '❌ Plan Fails'}
                        </div>
                        {!whatIfResults.moreIncome.planSucceeds && (
                          <>
                            <div className="text-gray-700 mb-1">
                              <strong>Years Portfolio Would Last:</strong>{' '}
                              {whatIfResults.moreIncome.yearsLastedIsEstimate ? '~' : ''}
                              {whatIfResults.moreIncome.yearsLasted} years
                              {whatIfResults.moreIncome.yearsLastedIsEstimate ? ' (estimate)' : ''}
                            </div>
                            <div className="text-gray-700">
                              <strong>Required Portfolio Balance:</strong> ${whatIfResults.moreIncome.requiredBalance.toLocaleString()}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Change Annual Fees */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">Change Annual Fees</h4>
                    <p className="text-xs text-gray-600 mb-2">Enter fee change in percentage points (Δ Fees % pts). Example: 0.5 = +0.50%.</p>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={getInputValue(inputs.whatIfAnnualFees)}
                      onChange={(e) => {
                        if (!whatIfUnlocked) return;
                        const value = e.target.value;
                        if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                          handleInputChange('whatIfAnnualFees', value);
                        }
                      }}
                      onBlur={(e) => { if (whatIfUnlocked) { const num = parseFloat(e.target.value); handleInputChange('whatIfAnnualFees', Number.isFinite(num) ? num : 0); } }}
                      onFocus={(e) => { if (whatIfUnlocked) e.target.select(); }}
                      disabled={!whatIfUnlocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100"
                      placeholder="Δ Fees % pts (e.g., 0.5)"
                    />
                    <button
                      type="button"
                      onClick={runAnnualFeesWhatIf}
                      disabled={!whatIfUnlocked}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-500"
                    >
                      Run
                    </button>
                    {whatIfUnlocked && whatIfResults.annualFees && (
                      <div className="mt-3 text-sm">
                        {whatIfResults.annualFees.error ? (
                          <div className="text-red-600 font-medium">Error: {whatIfResults.annualFees.error}</div>
                        ) : (
                          <>
                            <div className="text-gray-700 mb-2">
                              <strong>Current Fees MSI:</strong> ${Number(whatIfResults.annualFees.currentMaxSustainableIncome||0).toLocaleString()}/yr
                            </div>
                            <div className="text-gray-700 mb-2">
                              <strong>With Added Fees MSI:</strong> ${Number(whatIfResults.annualFees.maxSustainableIncome||0).toLocaleString()}/yr
                            </div>
                            <div className={`text-gray-700 mb-2 ${(Number(whatIfResults.annualFees.impact)||0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              <strong>Income Impact:</strong> ${Math.abs(Number(whatIfResults.annualFees.impact)||0).toLocaleString()}/yr {(Number(whatIfResults.annualFees.impact)||0) < 0 ? 'reduction' : 'increase'}
                            </div>
                            <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                              <strong>Explanation:</strong> Compares MSI at current fees vs. with an extra {(Number(inputs.whatIfAnnualFees)||0)}% investment fee. Higher fees reduce sustainable income.
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                        
                  {/* Sequence of Returns Risk */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">Sequence of Returns Risk</h4>
                    <p className="text-xs text-gray-600 mb-2">Test the impact of poor returns in the first 3 years of retirement on your maximum sustainable income.</p>
                    <input
                      type="text"
                      value={inputs.whatIfSequenceRisk}
                      onChange={(e) => {
                        if (!whatIfUnlocked) return;
                        const value = e.target.value;
                        if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                          handleInputChange('whatIfSequenceRisk', value);
                        }
                      }}
                      onBlur={(e) => {
                        if (!whatIfUnlocked) return;
                        const value = e.target.value;
                        const numValue = parseFloat(value);
                        if (isNaN(numValue)) handleInputChange('whatIfSequenceRisk', 0);
                        else handleInputChange('whatIfSequenceRisk', Math.max(-99, Math.min(99, numValue)));
                      }}
                      onFocus={(e) => { if (whatIfUnlocked) e.target.select(); }}
                      disabled={!whatIfUnlocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100"
                      placeholder="Enter real return % (e.g., -20 for -20% real return)"
                    />
                    <button
                      type="button"
                      onClick={runSequenceRiskWhatIf}
                      disabled={!whatIfUnlocked}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-500"
                    >
                      Run
                    </button>
                    {whatIfUnlocked && whatIfResults.sequenceRisk && (
                      <div className="mt-3 text-sm">
                        <div className="text-gray-700 mb-2">
                          <strong>Balance After 3 Years:</strong> ${whatIfResults.sequenceRisk.balanceAfter3Years.toLocaleString()}
                        </div>
                        <div className="text-gray-700 mb-2">
                          <strong>Current Scenario MSI:</strong> ${whatIfResults.sequenceRisk.currentMaxSustainableIncome.toLocaleString()}/yr
                        </div>
                        <div className="text-gray-700 mb-2">
                          <strong>MSI After Sequence Risk:</strong> ${whatIfResults.sequenceRisk.maxSustainableIncome.toLocaleString()}/yr
                        </div>
                        {whatIfResults.sequenceRisk.showYearlyBreakdown && (
                          <>
                            <div className="text-gray-700 mb-2">
                              <strong>Years 1-{whatIfResults.sequenceRisk.gapYears}:</strong> ${whatIfResults.sequenceRisk.incomeBeforePensionAge.toLocaleString()}/yr
                              <div className="text-xs text-gray-600">Outside income only (no super, no pension yet)</div>
                            </div>
                            <div className="text-gray-700 mb-2">
                              <strong>Years {whatIfResults.sequenceRisk.gapYears + 1}+:</strong> ${whatIfResults.sequenceRisk.incomeAfterPensionAge.toLocaleString()}/yr
                              <div className="text-xs text-gray-600">Outside income + Age Pension</div>
                            </div>
                          </>
                        )}
                        <div className={`text-gray-700 mb-2 ${whatIfResults.sequenceRisk.impact < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          <strong>Impact:</strong> ${whatIfResults.sequenceRisk.impact.toLocaleString()}/yr {whatIfResults.sequenceRisk.impact < 0 ? '(reduction)' : '(increase)'}
                        </div>
                        <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <strong>Explanation:</strong> This shows the impact of experiencing {inputs.whatIfSequenceRisk}% real returns for the first 3 years of retirement.
                          <br/>• Your balance is reduced to ${whatIfResults.sequenceRisk.balanceAfter3Years.toLocaleString()}
                          {whatIfResults.sequenceRisk.showYearlyBreakdown ? (
                            <>
                              <br/>• Years 1-{whatIfResults.sequenceRisk.gapYears}: Only ${whatIfResults.sequenceRisk.incomeBeforePensionAge.toLocaleString()}/yr (outside income only - no pension before age 67)
                              <br/>• Years {whatIfResults.sequenceRisk.gapYears + 1}+: ${whatIfResults.sequenceRisk.incomeAfterPensionAge.toLocaleString()}/yr (outside income + pension)
                              <br/>• <strong>Note:</strong> The MSI shown (${whatIfResults.sequenceRisk.maxSustainableIncome.toLocaleString()}/yr) represents the <em>minimum income across all years</em>. Your actual income will be higher in most years once the pension kicks in at age 67.
                            </>
                          ) : (
                            <>
                              <br/>• This calculation uses the exact same logic as the main calculator but with the reduced balance
                              <br/>• The MSI shown is what you could sustainably withdraw each year over the full retirement period
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Surviving Spouse Scenario */}
                  {inputs.relationshipStatus === 'couple' && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-2">Surviving Spouse</h4>
                      <p className="text-xs text-gray-600 mb-3">What if one partner dies? Calculate sustainable income for surviving spouse with combined super.</p>
                      <button
                        type="button"
                        onClick={runSurvivingSpouseWhatIf}
                        disabled={!whatIfUnlocked}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-500"
                      >
                        Run Scenario
                      </button>
                      {whatIfUnlocked && whatIfResults.survivingSpouse && !whatIfResults.survivingSpouse.error && (
                        <div className="mt-3 text-sm">
                          <div className="text-gray-700 mb-2">
                            <strong>Couple Income:</strong> ${whatIfResults.survivingSpouse.coupleIncome.toLocaleString()}/yr
                          </div>
                          <div className="text-gray-700 mb-2">
                            <strong>Surviving Spouse Income:</strong> ${whatIfResults.survivingSpouse.survivingSpouseIncome.toLocaleString()}/yr
                          </div>
                          <div className={`text-gray-700 mb-2 ${whatIfResults.survivingSpouse.incomeChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            <strong>Change:</strong> {whatIfResults.survivingSpouse.incomeChange >= 0 ? '+' : ''}${whatIfResults.survivingSpouse.incomeChange.toLocaleString()}/yr ({whatIfResults.survivingSpouse.percentChange}%)
                          </div>
                          <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                            <strong>Explanation:</strong> {whatIfResults.survivingSpouse.outcome}
                          </div>
                        </div>
                      )}
                      {whatIfResults.survivingSpouse && whatIfResults.survivingSpouse.error && (
                        <div className="mt-3 text-sm text-red-600">
                          {whatIfResults.survivingSpouse.error}
                        </div>
                      )}
                    </div>
                  )}
            </div>
          </div>
          );
        } ) () }

        {/* PDF Export and Continue Buttons (only show after projection run) */}
        {hasRunProjection && chartData.length > 0 && (
          <div className="mt-4 text-center">
            <div className="flex justify-center gap-3 flex-wrap">
              <button
                onClick={async () => {
                  if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                    startPdfUnlockCheckout({
                      calculatorTier: 'basic',
                      surface: 'basic_pdf_export',
                    }).catch(alertCheckoutFailure);
                    return;
                  }
                  try {
                    setIsExporting(true);
                    
                    // Wait for spinner to render
                    await new Promise(resolve => {
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          setTimeout(resolve, 100);
                        });
                      });
                    });
                    
                    // Wait for charts to render
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    
                    // Use same chart capture method as advanced calculator
                    const chartImages = {};
                    
                    const svgToImage = (svgElement, width, height) => {
                      return new Promise((resolve) => {
                        try {
                          const svgString = new XMLSerializer().serializeToString(svgElement);
                          const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                          const url = URL.createObjectURL(svgBlob);
                          const img = new Image();
                          img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = width;
                            canvas.height = height;
                            const ctx = canvas.getContext('2d');
                            ctx.fillStyle = '#ffffff';
                            ctx.fillRect(0, 0, width, height);
                            ctx.drawImage(img, 0, 0);
                            const imageData = canvas.toDataURL('image/png');
                            URL.revokeObjectURL(url);
                            resolve({
                              data: imageData,
                              width: width,
                              height: height
                            });
                          };
                          img.onerror = () => {
                            URL.revokeObjectURL(url);
                            resolve(null);
                          };
                          img.src = url;
                        } catch (err) {
                          console.error('[PDF] SVG conversion error:', err);
                          resolve(null);
                        }
                      });
                    };
                    
                    const captureChart = async (containerId, chartName) => {
                      const container = document.getElementById(containerId);
                      if (!container) {
                        console.warn(`[PDF] ${chartName} container not found: ${containerId}`);
                        return null;
                      }
                      
                      // Scroll into view
                      container.scrollIntoView({ behavior: 'instant', block: 'center' });
                      await new Promise(resolve => setTimeout(resolve, 500));
                      
                      // Hide tooltips
                      const tooltips = container.querySelectorAll('.recharts-tooltip-wrapper, [role="tooltip"]');
                      tooltips.forEach(t => t.style.display = 'none');
                      await new Promise(resolve => setTimeout(resolve, 200));
                      
                      // Try to find SVG element (Recharts uses SVG)
                      const svg = container.querySelector('svg');
                      if (svg) {
                        console.log(`[PDF] Found SVG for ${chartName}, converting...`);
                        const svgRect = svg.getBoundingClientRect();
                        const width = Math.max(svgRect.width || 800, 800);
                        const height = Math.max(svgRect.height || 300, 300);
                        
                        const result = await svgToImage(svg, width, height);
                        if (result) {
                          console.log(`[PDF] ${chartName} captured via SVG: ${result.width}x${result.height}`);
                          tooltips.forEach(t => t.style.display = '');
                          return result;
                        }
                      }
                      
                      // Fallback to html2canvas if SVG method fails
                      if (window.html2canvas) {
                        try {
                          console.log(`[PDF] Falling back to html2canvas for ${chartName}...`);
                          const canvas = await window.html2canvas(container, {
                            scale: 2,
                            backgroundColor: '#ffffff',
                            logging: true,
                            useCORS: true,
                            allowTaint: true
                          });
                          const imageData = canvas.toDataURL('image/png');
                          console.log(`[PDF] ${chartName} captured via html2canvas: ${canvas.width}x${canvas.height}`);
                          tooltips.forEach(t => t.style.display = '');
                          return {
                            data: imageData,
                            width: canvas.width,
                            height: canvas.height
                          };
                        } catch (err) {
                          console.error(`[PDF] html2canvas error for ${chartName}:`, err);
                          tooltips.forEach(t => t.style.display = '');
                          return null;
                        }
                      }
                      
                      tooltips.forEach(t => t.style.display = '');
                      return null;
                    };
                    
                    // Capture all charts
                    console.log('[PDF] Starting chart capture for basic calculator...');
                    chartImages.balanceChart = await captureChart('basic-balance-chart', 'Balance Chart');
                    chartImages.incomeChart = await captureChart('basic-income-chart-msi', 'Income Chart (MSI)');
                    chartImages.incomeChartTarget = await captureChart('basic-income-chart-target', 'Income Chart (Target)');
                    
                    const capturedCount = Object.keys(chartImages).filter(k => chartImages[k] !== null).length;
                    console.log(`[PDF] Chart capture complete: ${capturedCount} of 3 charts captured`);
                    if (capturedCount === 0) {
                      console.error('[PDF] WARNING: NO CHARTS CAPTURED! Check console for errors.');
                    }

                  const pdfSnap = lastRunInputsRef.current || inputs;
                  const pdfCtx = buildBasicCalcContext(pdfSnap, getAfterTaxIncome);
                  const calculatedMSI = lastRunMSIRef.current || Math.round(computeBasicMSI(pdfCtx));

                          // Calculate success rate (match UI logic: >= 0 is success)
                          const finalBalance = chartData[chartData.length - 1]?.balance || 0;
                          const calculatedSuccessRate = finalBalance >= 0 ? 100 : 0;

                  // Calculate average age pension from chart data
                  const avgPension = chartData.length > 0 
                    ? Math.round(chartData.reduce((sum, y) => sum + (y.pension || 0), 0) / chartData.length)
                    : 0;

                  // Enhanced inputs with results
                  const enhancedInputs = {
                    ...pdfSnap,
                    maxSustainableIncome: calculatedMSI,
                    successRate: calculatedSuccessRate,
                    agePensionEstimate: avgPension
                  };

                  PDFExportManager.exportBasicCalculator(enhancedInputs, chartData, whatIfResults, chartImages);
                  
                  setIsExporting(false);
                  
                  } catch (error) {
                    console.error('PDF export error:', error);
                    setIsExporting(false);
                    alert('Error generating PDF. Please try again.');
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-base transition-colors shadow-lg inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </button>
              
              <button
                onClick={() => {
                  const transferData = CalculatorBridge.basicToAdvanced(inputs);
                  CalculatorBridge.saveTransferData('advanced', transferData);
                  alert('Data transferred! Switch to Advanced Retirement Calculator for Monte Carlo analysis and dynamic strategies.');
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg text-base transition-colors shadow-lg inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Upgrade to Advanced
              </button>

              <div ref={exportMenuRef} className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-lg text-base transition-colors shadow-lg inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <button
                      onClick={async () => {
                        setShowExportMenu(false);
                        if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                          startPdfUnlockCheckout({
                            calculatorTier: 'basic',
                            surface: 'basic_pdf_export_menu',
                          }).catch(alertCheckoutFailure);
                          return;
                        }
                        try {
                          // Wait for charts to render
                          await new Promise(resolve => setTimeout(resolve, 1500));
                          
                          // Use same chart capture method as advanced calculator
                          const chartImages = {};
                          
                          const svgToImage = (svgElement, width, height) => {
                            return new Promise((resolve) => {
                              try {
                                const svgString = new XMLSerializer().serializeToString(svgElement);
                                const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                                const url = URL.createObjectURL(svgBlob);
                                const img = new Image();
                                img.onload = () => {
                                  const canvas = document.createElement('canvas');
                                  canvas.width = width;
                                  canvas.height = height;
                                  const ctx = canvas.getContext('2d');
                                  ctx.fillStyle = '#ffffff';
                                  ctx.fillRect(0, 0, width, height);
                                  ctx.drawImage(img, 0, 0);
                                  const imageData = canvas.toDataURL('image/png');
                                  URL.revokeObjectURL(url);
                                  resolve({
                                    data: imageData,
                                    width: width,
                                    height: height
                                  });
                                };
                                img.onerror = () => {
                                  URL.revokeObjectURL(url);
                                  resolve(null);
                                };
                                img.src = url;
                              } catch (err) {
                                console.error('[PDF] SVG conversion error:', err);
                                resolve(null);
                              }
                            });
                          };
                          
                          const captureChart = async (containerId, chartName) => {
                            const container = document.getElementById(containerId);
                            if (!container) {
                              console.warn(`[PDF] ${chartName} container not found: ${containerId}`);
                              return null;
                            }
                            
                            // Scroll into view
                            container.scrollIntoView({ behavior: 'instant', block: 'center' });
                            await new Promise(resolve => setTimeout(resolve, 500));
                            
                            // Hide tooltips
                            const tooltips = container.querySelectorAll('.recharts-tooltip-wrapper, [role="tooltip"]');
                            tooltips.forEach(t => t.style.display = 'none');
                            await new Promise(resolve => setTimeout(resolve, 200));
                            
                            // Try to find SVG element (Recharts uses SVG)
                            const svg = container.querySelector('svg');
                            if (svg) {
                              console.log(`[PDF] Found SVG for ${chartName}, converting...`);
                              const svgRect = svg.getBoundingClientRect();
                              const width = Math.max(svgRect.width || 800, 800);
                              const height = Math.max(svgRect.height || 300, 300);
                              
                              const result = await svgToImage(svg, width, height);
                              if (result) {
                                console.log(`[PDF] ${chartName} captured via SVG: ${result.width}x${result.height}`);
                                tooltips.forEach(t => t.style.display = '');
                                return result;
                              }
                            }
                            
                            // Fallback to html2canvas if SVG method fails
                            if (window.html2canvas) {
                              try {
                                console.log(`[PDF] Falling back to html2canvas for ${chartName}...`);
                                const canvas = await window.html2canvas(container, {
                                  scale: 2,
                                  backgroundColor: '#ffffff',
                                  logging: true,
                                  useCORS: true,
                                  allowTaint: true
                                });
                                const imageData = canvas.toDataURL('image/png');
                                console.log(`[PDF] ${chartName} captured via html2canvas: ${canvas.width}x${canvas.height}`);
                                tooltips.forEach(t => t.style.display = '');
                                return {
                                  data: imageData,
                                  width: canvas.width,
                                  height: canvas.height
                                };
                              } catch (err) {
                                console.error(`[PDF] html2canvas error for ${chartName}:`, err);
                                tooltips.forEach(t => t.style.display = '');
                                return null;
                              }
                            }
                            
                            tooltips.forEach(t => t.style.display = '');
                            return null;
                          };
                          
                          // Capture all charts
                          console.log('[PDF] Starting chart capture for basic calculator...');
                          chartImages.balanceChart = await captureChart('basic-balance-chart', 'Balance Chart');
                          chartImages.incomeChart = await captureChart('basic-income-chart-msi', 'Income Chart (MSI)');
                          chartImages.incomeChartTarget = await captureChart('basic-income-chart-target', 'Income Chart (Target)');
                          
                          const capturedCount = Object.keys(chartImages).filter(k => chartImages[k] !== null).length;
                          console.log(`[PDF] Chart capture complete: ${capturedCount} of 3 charts captured`);
                          if (capturedCount === 0) {
                            console.error('[PDF] WARNING: NO CHARTS CAPTURED! Check console for errors.');
                          }
                          
                          const pdfSnap = lastRunInputsRef.current || inputs;
                          const pdfCtx = buildBasicCalcContext(pdfSnap, getAfterTaxIncome);
                          const calculatedMSI = lastRunMSIRef.current || Math.round(computeBasicMSI(pdfCtx));
                          
                          const finalBalance = chartData[chartData.length - 1]?.balance || 0;
                          const calculatedSuccessRate = finalBalance >= 0 ? 100 : 0;
                          const avgPension = chartData.length > 0 
                            ? Math.round(chartData.reduce((sum, y) => sum + (y.pension || 0), 0) / chartData.length)
                            : 0;
                          
                          const enhancedInputs = {
                            ...pdfSnap,
                            maxSustainableIncome: calculatedMSI,
                            successRate: calculatedSuccessRate,
                            agePensionEstimate: avgPension
                          };
                          
                          PDFExportManager.exportBasicCalculator(enhancedInputs, chartData, whatIfResults, chartImages);
                        } catch (error) {
                          console.error('Error generating PDF:', error);
                          alert('Error generating PDF: ' + error.message);
                        }
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 font-semibold flex items-center gap-2 rounded-t-lg"
                    >
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Export PDF
                    </button>
                    <button
                      onClick={() => {
                        setShowExportMenu(false);
                        if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                          startPdfUnlockCheckout({
                            calculatorTier: 'basic',
                            surface: 'basic_csv_export_menu',
                          }).catch(alertCheckoutFailure);
                          return;
                        }
                        PDFExportManager.exportBasicToCSV(inputs, chartData);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 font-semibold flex items-center gap-2 rounded-b-lg border-t border-gray-100"
                    >
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export CSV
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {hasRunProjection && chartData.length > 0 && (
          <CalculationTransparency
            kind="basic"
            inputs={getInputsForAdvancedTransfer()}
            results={{
              chartData,
              maxSustainableIncome: lastRunMSIRef.current || chartData[0]?.maxSustainableIncome || chartData[0]?.totalIncome || 0,
              whatIfResults
            }}
            chartData={chartData}
            className="mt-6 text-left"
          />
        )}
      </div>
      
      {/* Autosave Indicator (subscriber benefit) */}
      {lastSaved && (
        <div className="text-center text-xs text-gray-500 mt-4 pb-4">
          Last saved: {Math.floor((new Date() - lastSaved) / 1000)} seconds ago — subscriber benefit
        </div>
      )}
    </div>

    {/* Tutorial – draggable panel (same as Advanced) */}
    {showTutorial && (
      <DraggableTutorialPanel onClose={() => setShowTutorial(false)} title="Basic Calculator Tutorial">
        <BasicTutorialGuide />
      </DraggableTutorialPanel>
    )}

      {/* Loading Spinner - Calculations */}
      {isCalculating && (
        <MobileLoadingSpinner 
          message="Calculating your retirement projection..."
          subMessage="Please wait a moment"
        />
      )}
      
      {/* Loading Spinner - PDF Export */}
      {isExporting && (
        <MobileLoadingSpinner 
          message="Generating PDF..."
          subMessage="Please wait while we create your report..."
        />
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Instructional Popup - Press Run Basic Projection */}
      {showInstructionalPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowInstructionalPopup(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowInstructionalPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              {/* Icon */}
              <div className="mb-4 flex justify-center">
                <div className="bg-blue-100 rounded-full p-4">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Your Inputs Are Ready!
              </h2>

              {/* Message */}
              <p className="text-gray-600 mb-6 text-lg">
                {isFromLanding ? (
                  <>
                    We've pre-filled your calculator with the values from the landing page. 
                    <br /><br />
                    <strong className="text-blue-600">Press "Run Basic Projection"</strong> below to see your personalized results.
                  </>
                ) : isFromMonteCarloDemo ? (
                  <>
                    The calculator is pre-filled with inputs (67-year-old single male, $1M super). 
                    <br /><br />
                    <strong className="text-blue-600">Click "Run Advanced Projection"</strong> for Historical Simulation, then try <strong>"Run Monte Carlo"</strong> to compare both methods!
                  </>
                ) : (
                  <>
                    The calculator is ready — enter your numbers below.
                    <br /><br />
                    <strong className="text-blue-600">Click "Run Advanced Projection"</strong> to see the results!
                  </>
                )}
              </p>

              {/* Button */}
              <button
                onClick={() => {
                  setShowInstructionalPopup(false);
                  if (isFromLanding) {
                    // Run the calculation immediately for basic calculator
                    setTimeout(() => {
                      runProjection();
                      // Scroll to results
                      if (projectionResultsRef.current) {
                        scrollToElement(projectionResultsRef, { delay: 300, offset: 60 });
                      }
                    }, 100);
                  }
                }}
                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {isFromLanding ? "Got it! Let me calculate" : "Got it! Show me the comparison"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inputs revert / persistence explainer (free users only, after run) */}
      {showInputsRevertModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowInputsRevertModal(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowInputsRevertModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="bg-blue-100 rounded-full p-4">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">How your inputs work</h2>
              <p className="text-gray-600 mb-6 text-left">
                <strong>Free users:</strong> Your inputs reset to defaults after each run. Re-enter your numbers each time you want to recalculate.
                <br /><br />
                <strong>Subscribers:</strong> Your inputs stay on the page and are saved automatically so you can return anytime without re-entering.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowInputsRevertModal(false)}
                  className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Got it
                </button>
                <a
                  href="#pricing"
                  onClick={() => setShowInputsRevertModal(false)}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-center"
                >
                  Subscribe
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FOMO Modal - Upgrade to Advanced */}
      {showFOMOModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setShowFOMOModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 rounded-2xl max-w-2xl w-full shadow-2xl border-2 border-yellow-400 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Animated background effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-yellow-400 via-transparent to-transparent animate-pulse"></div>
            </div>

            {/* Close button */}
            <button
              onClick={() => setShowFOMOModal(false)}
              className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="relative p-8">
              {/* Warning badge */}
              <div className="flex justify-center mb-4">
                <div className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2 animate-bounce">
                  <span className="text-xl">⚠️</span>
                  YOU'RE MISSING OUT
                </div>
              </div>

              {/* Main heading */}
              <h2 className="text-3xl font-bold text-white text-center mb-3">
                Getting <span className="text-yellow-400">Basic Results</span> Only
              </h2>
              <p className="text-gray-300 text-center mb-6 text-lg">
                The Basic Calculator gives simple projections, but is that enough?
              </p>

              {/* Risk questions */}
              <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <span className="text-2xl">💥</span>
                  <p className="text-white font-semibold">What if the market crashes in your first 3 years?</p>
                </div>
                <div className="flex items-start gap-3 mb-2">
                  <span className="text-2xl">⏰</span>
                  <p className="text-white font-semibold">What if you live to 95 instead of 86?</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📊</span>
                  <p className="text-white font-semibold">What about phased retirement strategies?</p>
                </div>
              </div>

              {/* Advanced features */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-3xl">🎯</span>
                  <h3 className="text-xl font-bold text-yellow-400">Advanced Calculator: Your First Run Is FREE!</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-green-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Monte Carlo - 1000+ scenarios</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>98 years of REAL market data</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Sequence risk analysis</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Phased retirement planning</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Professional PDF reports</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Withdrawal strategies</span>
                  </div>
                </div>
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setShowFOMOModal(false);
                    CalculatorBridge.navigateBasicToAdvanced(getInputsForAdvancedTransfer(), { eventLabel: 'FOMO modal' });
                  }}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 shadow-lg text-lg flex items-center justify-center gap-2"
                >
                  <span>🚀</span>
                  Continue in the retirement calculator →
                </button>
                <button
                  onClick={() => setShowFOMOModal(false)}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-4 px-6 rounded-xl transition-all"
                >
                  Stay with this result
                </button>
              </div>

              {/* Small print */}
              <p className="text-center text-gray-400 text-xs mt-4">
                No credit card required • Try all features free
              </p>
            </div>
          </div>
        </div>
      )}
    </ScenarioWrapper>
  );
}

export default App;
